"use client";
import { Fragment, useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
} from "@mui/material";
import {
  Filter,
  Download,
  Save,
  X,
  Search,
  Tally3 as Columns3,
  RefreshCw,
  Edit,
  Trash2,
  Copy,
  Info,
  Eye,
  View,
  User,
  Users,
  Calendar,
  ListChecks,
  Printer,
  Wallet,
  ClipboardCheck,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import toast from "react-hot-toast";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import {
  saveLayoutToConfig,
  resetLayoutToDefault,
  getUserConfig,
  getDefaultConfig,
  getTableConfig,
} from "../../Configurations/TableDataConfig";
import PushPinIcon from "@mui/icons-material/PushPin";
import { FilterBuilder } from "../filters";
import {
  OPERATORS,
  operatorRequiresMultipleValues,
  operatorRequiresValue,
  splitCommaSeparatedFilterValue,
} from "../filters/operators";
import ActiveFiltersBar from "./ActiveFiltersBar";
import SavedViewsBar from "./SavedViewsBar";
import Toolbar from "./Toolbar";
import TablePagination from "./TablePagination";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import {
  isLeadsListTable,
  renderLeadsListTableCell,
} from "../../Pages/EmsOptimized/List/leadsListTableCells";
import { isWithinLeadContactWindows } from "../../Pages/EmsOptimized/List/LeadListContactCell";
import {
  getStudentAccordionRows,
  getStudentRowId,
  isStudentsListTable,
  StudentAccordionCollapseRow,
  StudentExpandHeaderCell,
  StudentExpandToggleCell,
} from "./studentListAccordion";
import {
  compareNumericStringValues,
  shouldSortAsNumericString,
} from "../../Utils/metadataListSort";

// Constants to avoid recreating objects
const DEFAULT_ROWS_PER_PAGE = 10;

const formatLabel = (label) => {
  return label.replace(/_/g, " ");
};

function getRowActiveForToggle(row) {
  if (!row) return false;
  if (row.__is_active_for_toggle !== undefined) {
    return Boolean(row.__is_active_for_toggle);
  }
  const v = row.is_active;
  if (v === true || v === 1 || v === "1") return true;
  if (v === "Active") return true;
  return false;
}

function isToggleActiveDisabled(row) {
  return Boolean(row?.__toggle_active_disabled) && getRowActiveForToggle(row);
}

function getToggleActiveTooltip(row) {
  if (isToggleActiveDisabled(row)) {
    return (
      row?.__toggle_active_disabled_reason ||
      "Cannot deactivate this user"
    );
  }
  return getRowActiveForToggle(row) ? "Deactivate" : "Activate";
}

/** Show "-" for missing values; avoid String(null) → "null" in table cells. */
const formatCellDisplay = (value) => {
  if (value === null || value === undefined) return "-";
  if (value === "") return "-";
  if (typeof value === "string") {
    const t = value.trim();
    if (t === "" || t.toLowerCase() === "null" || t.toLowerCase() === "undefined") {
      return "-";
    }
  }
  return String(value);
};

function normalizeEmailForMailto(raw) {
  let s = String(raw ?? "").trim();
  if (!s || s === "-" || !s.includes("@")) return null;
  s = s.replace(/[.,;:\s]+$/g, "");
  if (!s.includes("@")) return null;
  const [local, ...rest] = s.split("@");
  const domain = rest.join("@");
  if (!local || !domain || !domain.includes(".")) return null;
  return `${local}@${domain}`;
}

function buildMailtoHref(email) {
  const normalized = normalizeEmailForMailto(email);
  if (!normalized) return null;
  return `mailto:${normalized}`;
}

function buildTelHref(phone) {
  const s = String(phone ?? "").trim();
  if (!s || s === "-") return null;
  const normalized = s.startsWith("+")
    ? `+${s.slice(1).replace(/\D/g, "")}`
    : s.replace(/\D/g, "");
  if (!normalized || normalized === "+") return null;
  return `tel:${normalized}`;
}

function isEmailColumn(col) {
  const dt = col?.dataType;
  const key = String(col?.key || "").toLowerCase();
  return (
    dt === "email" ||
    key === "email" ||
    key.endsWith("_email") ||
    key.includes("email")
  );
}

function looksLikeEmailAddress(s) {
  const t = String(s ?? "").trim();
  if (!t || t.includes(" ") || !t.includes("@")) return false;
  const parts = t.split("@");
  if (parts.length !== 2 || !parts[0] || !parts[1]) return false;
  return parts[1].includes(".");
}

function looksLikeDateOnlyString(s) {
  const t = String(s ?? "").trim();
  if (!t) return false;
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return true;
  if (/^\d{2}-\d{2}-\d{4}$/.test(t)) return true;
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(t)) return true;
  return false;
}

function isPhoneColumn(col) {
  const key = String(col?.key || "").toLowerCase();
  if (
    key.includes("date") ||
    key.includes("time") ||
    key.endsWith("_at") ||
    key === "demo_date" ||
    key === "admission_date" ||
    key === "lead_datetime"
  ) {
    return false;
  }
  const dt = col?.dataType;
  if (
    key === "employee_code" ||
    key === "person_code" ||
    key.endsWith("_code")
  ) {
    return false;
  }
  return (
    dt === "phone" ||
    key === "phone" ||
    key === "phone_number" ||
    key === "alternate_phone" ||
    key.endsWith("_phone")
  );
}

function isFeeOrMoneyTableColumn(col) {
  const key = String(col?.key || "").toLowerCase();
  if (!key) return false;
  if (key.includes("net_fee")) return true;
  if (key.includes("fee_inr")) return true;
  if (key.includes("fee_instalment") || key.includes("fee_installment")) return true;
  if (key.includes("total_fee")) return true;
  if (key.includes("discount_amount")) return true;
  if (key === "gateway_charges" || key === "forex_difference") return true;
  if (key.includes("amount_received_inr")) return true;
  if (key.includes("amount_paid_student")) return true;
  if (key.includes("settlement") && key.includes("amount")) return true;
  if (key.endsWith("_fee") && !key.includes("feedback")) return true;
  return false;
}

function looksLikeDecimalAmountString(s) {
  const t = String(s ?? "").trim().replace(/,/g, "");
  return /^\d+\.\d{1,4}$/.test(t);
}

function openMailOrTelLink(e, href) {
  e.stopPropagation();
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
    return;
  }
  e.preventDefault();
  window.location.assign(href);
}

const NON_LINKABLE_CELL_KEYS = new Set([
  "employee_code",
  "person_code",
  "emp_code",
  "payment_number",
  "receipt_number",
  "student_fee_payment_number",
  "admission_number",
  "admission",
  "capacity",
  "fee_inr",
  "net_fee_amount",
  "net_fee_amount_student_currency",
  "total_fee_amount",
  "discount_amount",
  "total_fee_amount_student_currency",
  "discount_amount_student_currency",
  "contact_status_dot",
  "contact_timing_live",
]);

const mailTelLinkSx = {
  color: "primary.main",
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  cursor: "pointer",
  "&:hover": { opacity: 0.88 },
};

// Helper function to construct proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return "https://avatar.iran.liara.run/public/47";

  // If it's already a full URL (starts with http), return as is
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // If it starts with /storage/, construct full URL with backend
  if (imagePath.startsWith("/storage/app/public")) {
    return `${MAIN_URL}${imagePath}`;
  }

  // If it's just a filename or relative path, construct full URL
  if (imagePath.startsWith("storage/")) {
    return `${MAIN_URL}/${imagePath}`;
  }

  // If it's just a filename, assume it's in storage/employees/
  return `${MAIN_URL}/storage/app/public/employees${imagePath}`;
};

const sortConfigFromUrl = (params, fallback = null) => {
  const sortBy = params?.get?.("sortBy");
  const sortOrder = params?.get?.("sortOrder");
  if (sortBy && sortOrder) {
    return [{ key: sortBy, direction: sortOrder }];
  }
  return fallback;
};

const Customisetable = ({
  Route,
  configss,
  onFilterChange,
  onSortChange,
  onPageChange,
  DeleteFunc,
  onclickRow,
  EditFunc,
  copyFunction,
  viewFunction = undefined,
  infoFunction = undefined,
  info_action: infoActionProp = undefined,
  configuration,
  tableName,
  exportTitle,
  recordKey,
  recordLabel,
  mainKey,
  title,
  linkType,
  mainData,
  showActions,
  showViewButton = true,
  showEditButton = true,
  showDeleteButton = true,
  showReceiptButton = false,
  showEnrolledButton = false,
  showSnapshotButton = false,
  showLeadActivityButton = false,
  showAdmissionButton = false,
  showDemoParticipantButton = false,
  showFeePortalButton = false,
  showBatchAttendanceButton = false,
  showBatchClassesButton = false,
  showBatchStudentsButton = false,
  showBatchRecordsButton = false,
  ReceiptFunc,
  EnrolledFunc,
  SnapshotFunc,
  LeadActivityFunc,
  AdmissionFunc,
  DemoParticipantFunc,
  FeePortalFunc,
  BatchAttendanceFunc,
  BatchClassesFunc,
  BatchStudentsFunc,
  BatchRecordsFunc,
  CardColoumn = [],
  hideToolbar,
  hideActiveFiltersBar = false,
  hideFilterButton = false,
  hidePersonalizeView = false,
  onSaveLayout,
  handleShow,
  onResetLayout,
  loadedBackendConfig,
  datagridKey = "",
  paginationData,
  filterModule, // Optional: module name for filter builder (defaults to tableName)
  apiResolvedFilterLabels = {},
  toggleActiveFn,
  searchPlaceholder = "Search...",
}) => {
  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();

  const hasInfoPermission = useMemo(() => {
    if (!infoActionProp) return true;
    return Permission && Permission.includes(infoActionProp);
  }, [infoActionProp, Permission]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isSmallScreen = isMobile || isTablet;

  const [expandedStudentRows, setExpandedStudentRows] = useState({});

  const navigate = useNavigate();

  const [view, setView] = useState("table");

  const [columns, setColumns] = useState([]);
  const [originalColumnOrder, setOriginalColumnOrder] = useState([]);
  const columnLayoutSaveTimerRef = useRef(null);
  const columnLayoutReadyRef = useRef(false);
  const columnsRef = useRef([]);
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState(
    configuration?.[0]?.default_config.sortConfig || []
  );
  const [filters, setFilters] = useState({});
  const [pendingFilters, setPendingFilters] = useState({});
  const [pendingFilterBuilderFilters, setPendingFilterBuilderFilters] =
    useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [columnManagerOpen, setColumnManagerOpen] = useState(false);
  const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [resizingColumnKey, setResizingColumnKey] = useState(null);
  const initialMouseX = useRef(0);
  const initialColumnWidth = useRef(0);
  const [pendingColumns, setPendingColumns] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [leadActivityConfirmOpen, setLeadActivityConfirmOpen] = useState(false);
  const [pendingLeadActivityItem, setPendingLeadActivityItem] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const hasInitializedFromURL = useRef(false);

  const [backendPagination, setBackendPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const [lockedFilters, setLockedFilters] = useState({});
  const [viewSavedTrigger, setViewSavedTrigger] = useState(0);

  useEffect(() => {
    if (hasInitializedFromURL.current) return;

    const pageParam = Number.parseInt(searchParams.get("page")) || 1;
    const pageSizeParam =
      Number.parseInt(searchParams.get("pageSize")) || DEFAULT_ROWS_PER_PAGE;

    // Load search query from URL
    const searchParam = searchParams.get("search") || "";

    // Load filters from URL params
    const urlFilters = {};
    const urlOperators = {};
    searchParams.forEach((value, key) => {
      if (
        key.startsWith("filter_") &&
        !key.endsWith("_operator") &&
        !key.endsWith("_label")
      ) {
        const filterKey = key.replace("filter_", "");
        // Check if operator doesn't require value
        const operatorKey = `filter_${filterKey}_operator`;
        const operator = searchParams.get(operatorKey);
        const requiresValue = operator ? operatorRequiresValue(operator) : true;

        const isValidValue =
          value &&
          value !== "undefined" &&
          String(value).trim() !== "";
        // Include filter if value exists OR if operator doesn't require value
        if (isValidValue || !requiresValue) {
          urlFilters[filterKey] = isValidValue ? value : "";
          if (operator) {
            urlOperators[filterKey] = operator;
          }
        }
      }
    });

    setCurrentPage(pageParam);
    setRowsPerPage(pageSizeParam);
    setFilters(urlFilters);
    setSearchQuery(searchParam);
    setPendingFilters(urlFilters);

    const urlSort = sortConfigFromUrl(searchParams);
    if (urlSort) {
      setSortConfig(urlSort);
    }

    hasInitializedFromURL.current = true;
  }, []);

  useEffect(() => {
    if (paginationData) {
      setBackendPagination(paginationData);

      const urlPage = Number.parseInt(searchParams.get("page")) || 1;
      const urlPageSize =
        Number.parseInt(searchParams.get("pageSize")) || DEFAULT_ROWS_PER_PAGE;

      // Backend may return limit instead of pageSize
      const backendPageSize =
        paginationData.pageSize || paginationData.limit || urlPageSize;
      const backendPage = paginationData.page || urlPage;

      // Always trust URL params for page (user-initiated changes)
      // Only sync pageSize from backend if it matches URL
      setCurrentPage(urlPage);

      // For pageSize, prefer backend if it matches URL, otherwise use URL
      if (backendPageSize === urlPageSize || !paginationData.pageSize) {
        setRowsPerPage(urlPageSize);
      } else {
        setRowsPerPage(backendPageSize);
      }
    }
  }, [paginationData, searchParams]);

  // Helper function to update URL
  const updateURL = useCallback(
    (updates) => {
      const newSearchParams = new URLSearchParams(searchParams);

      // Update filters
      if (updates.filters !== undefined) {
        // Remove all existing filter params (including operators and logics)
        Array.from(newSearchParams.keys()).forEach((key) => {
          if (key.startsWith("filter_")) {
            newSearchParams.delete(key);
          }
        });

        // Add new filter params
        Object.entries(updates.filters).forEach(([key, value]) => {
          // Check if operator requires a value
          const operator = updates.filterOperators?.[key];
          const requiresValue = operator
            ? operatorRequiresValue(operator)
            : true;

          // For operators that don't require values, allow empty string
          // For operators that require values, only add if value is valid
          if (
            !requiresValue ||
            (value !== null && value !== undefined && value !== "")
          ) {
            const stringValue = Array.isArray(value)
              ? value.map((v) => String(v)).join(",")
              : value === null || value === undefined
                ? ""
                : String(value);
            // For operators that don't require values, use empty string as placeholder
            if (!requiresValue || stringValue.trim() !== "") {
              newSearchParams.set(
                `filter_${key}`,
                requiresValue ? stringValue : ""
              );
            }
          }
        });

        // Add filter operators if provided
        if (updates.filterOperators !== undefined) {
          Object.entries(updates.filterOperators).forEach(([key, operator]) => {
            if (operator) {
              // Add operator if filter exists OR if operator doesn't require value
              const requiresValue = operatorRequiresValue(operator);
              const hasFilter =
                updates.filters && updates.filters[key] !== undefined;
              if (hasFilter || !requiresValue) {
                newSearchParams.set(`filter_${key}_operator`, operator);
                // If operator doesn't require value, also add empty filter value
                if (
                  !requiresValue &&
                  (!updates.filters || !updates.filters[key])
                ) {
                  newSearchParams.set(`filter_${key}`, "");
                }
              }
            }
          });
        }

        // Human-readable labels for enum/FK filter values (e.g. Open instead of 1)
        if (updates.filterLabels !== undefined) {
          Object.entries(updates.filterLabels).forEach(([key, label]) => {
            if (label != null && String(label).trim() !== "") {
              newSearchParams.set(`filter_${key}_label`, String(label));
            }
          });
        }
      }

      // Update sort
      if (updates.sortConfig !== undefined && updates.sortConfig.length > 0) {
        newSearchParams.set("sortBy", updates.sortConfig[0].key);
        newSearchParams.set("sortOrder", updates.sortConfig[0].direction);
      }

      // Update search
      if (updates.searchQuery !== undefined) {
        if (updates.searchQuery.trim() !== "") {
          newSearchParams.set("search", updates.searchQuery);
        } else {
          newSearchParams.delete("search");
        }
      }

      // Update page
      if (updates.currentPage !== undefined) {
        if (updates.currentPage > 1) {
          newSearchParams.set("page", updates.currentPage.toString());
        } else {
          newSearchParams.delete("page");
        }
      }

      // Update rows per page
      if (updates.rowsPerPage !== undefined) {
        if (updates.rowsPerPage !== DEFAULT_ROWS_PER_PAGE) {
          newSearchParams.set("pageSize", updates.rowsPerPage.toString());
        } else {
          newSearchParams.delete("pageSize");
        }
      }

      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Cache for nested values to avoid recalculating
  const valueCache = useRef(new Map());

  const colors = useMemo(
    () => ({
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
      success: theme.palette.success.main,
      warning: theme.palette.warning.main,
      error: theme.palette.error.main,
      info: theme.palette.info.main,
      background: theme.palette.background.default,
      surface: theme.palette.background.paper,
      text: {
        primary: theme.palette.text.primary,
        secondary: theme.palette.text.secondary,
      },
      grey: {
        50:
          theme.palette.mode === "dark"
            ? theme.palette.grey[900]
            : theme.palette.grey[50],
        100:
          theme.palette.mode === "dark"
            ? theme.palette.grey[800]
            : theme.palette.grey[100],
        200:
          theme.palette.mode === "dark"
            ? theme.palette.grey[700]
            : theme.palette.grey[200],
        300:
          theme.palette.mode === "dark"
            ? theme.palette.grey[600]
            : theme.palette.grey[300],
        400:
          theme.palette.mode === "dark"
            ? theme.palette.grey[500]
            : theme.palette.grey[400],
        500:
          theme.palette.mode === "dark"
            ? theme.palette.grey[400]
            : theme.palette.grey[500],
        600:
          theme.palette.mode === "dark"
            ? theme.palette.grey[300]
            : theme.palette.grey[600],
        700:
          theme.palette.mode === "dark"
            ? theme.palette.grey[200]
            : theme.palette.grey[700],
        800:
          theme.palette.mode === "dark"
            ? theme.palette.grey[100]
            : theme.palette.grey[800],
        900:
          theme.palette.mode === "dark"
            ? theme.palette.grey[50]
            : theme.palette.grey[900],
      },
      departments: {
        Design: "#e91e63",
        HR: "#ff9800",
        Finance: "#4caf50",
        Marketing: "#9c27b0",
        Engineering: "#2196f3",
        Sales: "#ff5722",
        Support: "#795548",
        default: theme.palette.text.secondary,
      },
    }),
    [theme]
  );

  const mapBackendColumns = useCallback((config) => {
    const mandatoryColumnsArray = config.defaults?.mandatoryColumns || [];
    return [...(config.columns || [])]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((col) => ({
        ...col,
        key: col.key || col.field,
        field: col.field || col.key,
        sortable: col.sortable !== false,
        filterable: col.filterable !== false,
        mandatory:
          mandatoryColumnsArray.includes(col.key) ||
          mandatoryColumnsArray.includes(col.field),
      }));
  }, []);

  useEffect(() => {
    if (loadedBackendConfig && loadedBackendConfig.columns) {
      const backendColumns = mapBackendColumns(loadedBackendConfig);
      setColumns(backendColumns);
      setOriginalColumnOrder(backendColumns);

      if (loadedBackendConfig.sortConfig) {
        const nextSort = sortConfigFromUrl(
          searchParams,
          loadedBackendConfig.sortConfig
        );
        if (nextSort) setSortConfig(nextSort);
      }

      if (loadedBackendConfig.rowsPerPage) {
        setRowsPerPage(loadedBackendConfig.rowsPerPage);
      }

      // Filters are handled by a separate useEffect after loadFiltersFromConfig is defined
      // This avoids initialization order issues
    }
  }, [loadedBackendConfig, mapBackendColumns]);

  const getNestedValue = useCallback((obj, path) => {
    const cacheKey = `${obj?.[mainKey] || JSON.stringify(obj)}_${path}`;
    if (valueCache.current.has(cacheKey)) {
      return valueCache.current.get(cacheKey);
    }
    const directValue = path
      ?.split(".")
      .reduce((acc, part) => (acc ? acc[part] : undefined), obj);

    // If config uses human labels like "Assigned Employee" but API uses snake_case keys,
    // fall back to a normalized snake_case lookup.
    let value = directValue;
    if (value === undefined && typeof path === "string" && obj && typeof obj === "object") {
      const normalizedKey = path
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_]/g, "_")
        .replace(/_+/g, "_")
        .toLowerCase();

      if (normalizedKey && normalizedKey !== path && Object.prototype.hasOwnProperty.call(obj, normalizedKey)) {
        value = obj[normalizedKey];
      }
    }
    valueCache.current.set(cacheKey, value);
    return value;
  }, []);

  const leadsTable = useMemo(
    () => isLeadsListTable(tableName, configuration),
    [tableName, configuration]
  );

  const isStudentsTable = useMemo(
    () => isStudentsListTable(tableName),
    [tableName]
  );

  const batchesTable = useMemo(() => {
    const key = String(tableName || "").toLowerCase();
    const configTable = String(configuration?.[0]?.table || "").toLowerCase();
    return (
      key === "batches" ||
      key === "ems_batches" ||
      configTable === "batches" ||
      configTable === "ems_batches"
    );
  }, [tableName, configuration]);

  const batchClassesTable = useMemo(() => {
    const key = String(tableName || "").toLowerCase();
    const configTable = String(configuration?.[0]?.table || "").toLowerCase();
    return (
      key === "batch classes" ||
      key === "ems_batch_classes" ||
      configTable === "batch classes" ||
      configTable === "ems_batch_classes"
    );
  }, [tableName, configuration]);

  const batchStudentsTable = useMemo(() => {
    const key = String(tableName || "").toLowerCase();
    const configTable = String(configuration?.[0]?.table || "").toLowerCase();
    return (
      key === "batch students" ||
      key === "ems_batch_students" ||
      configTable === "batch students" ||
      configTable === "ems_batch_students"
    );
  }, [tableName, configuration]);

  const classRecordsTable = useMemo(() => {
    const key = String(tableName || "").toLowerCase();
    const configTable = String(configuration?.[0]?.table || "").toLowerCase();
    return (
      key === "class records" ||
      key === "ems_batch_class_records" ||
      configTable === "class records" ||
      configTable === "ems_batch_class_records"
    );
  }, [tableName, configuration]);

  const toggleStudentRow = useCallback((row, rowIndex) => {
    const rowId = getStudentRowId(row, mainKey, rowIndex);
    if (!rowId) return;
    setExpandedStudentRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  }, [mainKey]);

  const studentTableExtraCols = isStudentsTable ? 1 : 0;

  const renderLinkedCellContent = useCallback(
    (row, col) => {
      if (!leadsTable) return null;
      return renderLeadsListTableCell({
        row,
        col,
        getNestedValue,
        navigate,
      });
    },
    [leadsTable, getNestedValue, navigate]
  );

  const renderDataCellContent = useCallback(
    (row, col) => {
      const leadsContent = renderLinkedCellContent(row, col);
      if (leadsContent) return leadsContent;

      if (tableName === "ems_lead_activities") {
        if (col.key === "activity_type") {
          const val = String(getNestedValue(row, "activity_type") ?? "").trim();
          if (!val || val === "-") return <span style={{ color: "#aaa" }}>—</span>;
          const iconStyle = { width: 15, height: 15, flexShrink: 0, color: "#6b7280" };
          let icon = null;
          const lower = val.toLowerCase();
          if (lower.includes("call")) {
            icon = <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.9 9.9a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.83 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 5.61 5.61l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.99 16.92z"/></svg>;
          } else if (lower.includes("whatsapp")) {
            icon = <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
          } else if (lower.includes("email") || lower.includes("mail")) {
            icon = <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/></svg>;
          } else if (lower.includes("sms") || lower.includes("text")) {
            icon = <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
          } else if (lower.includes("meeting")) {
            icon = <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
          } else {
            icon = <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
          }
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {icon}
              <span style={{ fontSize: "0.875rem" }}>{val}</span>
            </Box>
          );
        }

        if (col.key === "lead_direction") {
          const val = String(getNestedValue(row, "lead_direction") ?? "").trim();
          if (!val || val === "-") return <span style={{ color: "#aaa" }}>—</span>;
          const isOutgoing = val.toLowerCase() === "outgoing";
          return (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, fontSize: "0.875rem" }}>
              <Box sx={{ width: 26, height: 26, borderRadius: "7px", background: isOutgoing ? "#e0faf4" : "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {isOutgoing
                  ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isOutgoing ? "#0a7c5c" : "#6d28d9"} strokeWidth="2.5" strokeLinecap="round"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6d28d9" strokeWidth="2.5" strokeLinecap="round"><line x1="17" y1="7" x2="7" y2="17"/><polyline points="17 17 7 17 7 7"/></svg>
                }
              </Box>
              {val}
            </Box>
          );
        }

        if (col.key === "status" || col.key === "call_status") {
          const val = String(getNestedValue(row, col.key) ?? "").trim();
          if (!val || val === "-") return <span style={{ color: "#aaa" }}>—</span>;
          const lower = val.toLowerCase();
          let dotColor = "#9ca3af";
          let bg = "#f3f4f6";
          let textColor = "#4b5563";
          if (lower.includes("attended") || lower.includes("answered") || lower.includes("completed")) {
            dotColor = "#22c55e"; bg = "#dcfce7"; textColor = "#15803d";
          } else if (lower === "no answer" || lower.includes("noanswer") || lower.includes("missed") || lower.includes("failed")) {
            dotColor = "#ef4444"; bg = "#fee2e2"; textColor = "#b91c1c";
          } else if (lower.includes("delivered") || lower.includes("responded")) {
            dotColor = "#0891b2"; bg = "#cffafe"; textColor = "#0e7490";
          } else if (lower.includes("read")) {
            dotColor = "#4C5565"; bg = "#ede9fe"; textColor = "#6d28d9";
          } else if (lower.includes("sent")) {
            dotColor = "#9ca3af"; bg = "#f3f4f6"; textColor = "#4b5563";
          } else if (lower.includes("busy")) {
            dotColor = "#f59e0b"; bg = "#fef3c7"; textColor = "#b45309";
          } else if (lower.includes("pending") || lower.includes("scheduled")) {
            dotColor = "#f59e0b"; bg = "#fef9c3"; textColor = "#b45309";
          }
          return (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1.2, py: 0.4, borderRadius: "999px", background: bg, fontSize: "0.8rem", fontWeight: 600, color: textColor, width: "fit-content" }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
              {val}
            </Box>
          );
        }
      }

      const isEmployeeNameCol = col.key === "name" && String(tableName || "").toLowerCase().includes("employee");
      const NAME_AVATAR_KEYS = ["lead_person_name", "activity_by", "student_name", "student", "batch_student", "trainer", "lead", "employee_name", "application_user_full_name"];
      if (NAME_AVATAR_KEYS.includes(col.key) || isEmployeeNameCol) {
        const name = String(getNestedValue(row, col.key) ?? "").trim() || "—";
        const parts = name.split(/\s+/).filter(Boolean);
        const alphaParts = parts.filter((p) => /^[a-zA-Z]/.test(p));
        const initials = alphaParts.length >= 2
          ? (alphaParts[0][0] + alphaParts[1][0]).toUpperCase()
          : alphaParts.length === 1
          ? alphaParts[0].slice(0, 2).toUpperCase()
          : name.slice(0, 2).toUpperCase();
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: "50%", background: "#4C5565", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, flexShrink: 0, letterSpacing: "0.03em", userSelect: "none" }}>
              {initials}
            </Box>
            <Box component="span" sx={{ fontSize: "0.875rem", wordBreak: "break-word" }}>
              {name}
            </Box>
          </Box>
        );
      }

      const raw = getNestedValue(row, col.key);
      const rawDisplay = raw === null || raw === undefined ? "" : String(raw);
      const display = formatCellDisplay(raw);

      const isLinkCol =
        col.key?.toLowerCase() === linkType ||
        col.label?.toLowerCase() === linkType;

      if (isLinkCol && onclickRow) {
        return (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              onclickRow(row);
            }}
            sx={{
              cursor: "pointer",
              color:
                theme.palette.mode === "dark" ? "skyblue" : "#4d75f5ff",
              textDecoration: "none",
            }}
            title={title}
          >
            {display}
          </Box>
        );
      }

      if (batchStudentsTable && col.key === "batch_code") {
        const batchCodeRaw =
          String(getNestedValue(row, "batch_code") ?? "").trim() || "-";
        const [batchCodeLineRaw, trainingProgramLineRaw] =
          batchCodeRaw.split("\n");
        const batchCodeLine = String(batchCodeLineRaw ?? "").trim() || "-";
        const trainingProgramLine =
          String(trainingProgramLineRaw ?? "").trim() || "-";
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              minWidth: 0,
              lineHeight: 1.25,
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: "0.875rem",
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}
            >
              {batchCodeLine}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: "0.78rem",
                color: "text.secondary",
                mt: 0.35,
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {trainingProgramLine}
            </Box>
          </Box>
        );
      }

      if (batchStudentsTable && col.key === "city_country") {
        const cityCountryRaw =
          String(getNestedValue(row, "city_country") ?? "").trim() || "-";
        const [countryPartRaw, cityPartRaw] = cityCountryRaw.split("/");
        const countryText = String(countryPartRaw ?? "").trim() || "-";
        const cityText = String(cityPartRaw ?? "").trim() || "-";
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              minWidth: 0,
              lineHeight: 1.25,
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: "0.875rem",
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}
            >
              {countryText}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: "0.78rem",
                color: "text.secondary",
                mt: 0.35,
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {cityText}
            </Box>
          </Box>
        );
      }

      if (classRecordsTable && col.key === "status") {
        const statusText =
          String(getNestedValue(row, "status") ?? "-").trim() || "-";
        const isCancelled = statusText.toLowerCase() === "cancelled";
        const cancelReason = String(
          getNestedValue(row, "status_reason_display") ??
            getNestedValue(row, "not_conducted_reason") ??
            ""
        ).trim();
        const showReason = isCancelled && cancelReason && cancelReason !== "-";

        if (!showReason) return statusText;

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              minWidth: 0,
              lineHeight: 1.25,
            }}
          >
            <Box component="span">{statusText}</Box>
            <Box
              component="span"
              sx={{
                fontSize: "0.78rem",
                color: "text.secondary",
                mt: 0.35,
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {cancelReason}
            </Box>
          </Box>
        );
      }

      if (NON_LINKABLE_CELL_KEYS.has(col?.key) || isFeeOrMoneyTableColumn(col)) {
        return display;
      }

      const empty =
        !rawDisplay ||
        rawDisplay === "-" ||
        rawDisplay === "null" ||
        rawDisplay === "N/A";

      const emailHref =
        !empty && (isEmailColumn(col) || looksLikeEmailAddress(rawDisplay))
          ? buildMailtoHref(rawDisplay)
          : null;
      if (emailHref) {
        return (
          <Box
            component="a"
            href={emailHref}
            title="Send email (opens your mail app)"
            onClick={(e) => openMailOrTelLink(e, emailHref)}
            sx={mailTelLinkSx}
          >
            {display}
          </Box>
        );
      }

      if (!empty && isPhoneColumn(col)) {
        if (looksLikeDateOnlyString(rawDisplay)) return display;
        if (looksLikeDecimalAmountString(rawDisplay)) return display;
        const telHref = buildTelHref(rawDisplay);
        if (telHref) {
          return (
            <Box
              component="a"
              href={telHref}
              title="Call (opens phone / calling app)"
              onClick={(e) => openMailOrTelLink(e, telHref)}
              sx={mailTelLinkSx}
            >
              {display}
            </Box>
          );
        }
      }

      if (batchesTable && col.key === "batch_code") {
        const batchId =
          row?.organization_ems_batch_id ??
          row?.id ??
          row?.batch?.organization_ems_batch_id;
        const hasBatchId =
          batchId !== null &&
          batchId !== undefined &&
          String(batchId).trim() !== "" &&
          String(batchId) !== "-";
        if (!empty && hasBatchId) {
          return (
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/batch-students?organization_ems_batch_id=${encodeURIComponent(
                    String(batchId)
                  )}`
                );
              }}
              sx={{
                p: 0,
                border: 0,
                background: "transparent",
                color: "primary.main",
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                cursor: "pointer",
                font: "inherit",
                textAlign: "left",
                "&:hover": { opacity: 0.88 },
              }}
            >
              {display}
            </Box>
          );
        }
      }

      if ((batchesTable || batchClassesTable) && col.key === "students") {
        const tip = getNestedValue(row, "__batch_students_tooltip");
        if (tip && String(tip).trim()) {
          return (
            <Tooltip
              title={
                <Box
                  component="span"
                  sx={{ whiteSpace: "pre-line", display: "block", maxWidth: 360 }}
                >
                  {String(tip)}
                </Box>
              }
              arrow
              enterDelay={200}
            >
              <Box
                component="span"
                sx={{
                  cursor: "help",
                  borderBottom: "1px dotted",
                  borderColor: "text.secondary",
                }}
              >
                {display}
              </Box>
            </Tooltip>
          );
        }
      }

      return display;
    },
    [
      renderLinkedCellContent,
      getNestedValue,
      linkType,
      onclickRow,
      theme.palette.mode,
      title,
      batchesTable,
      batchClassesTable,
      batchStudentsTable,
      classRecordsTable,
      navigate,
    ]
  );

  const loadDataFromConfiguration = useCallback(
    async (tableConfigName) => {
      setLoading(true);
      setData([]);
      setColumns([]);
      // setFilters({});
      // setPendingFilters({});
      // Server-side pagination: keep page from URL; client-side: reset to 1
      if (paginationData?.totalPages) {
        const urlPage = Number.parseInt(searchParams.get("page"), 10) || 1;
        setCurrentPage(urlPage);
      } else {
      setCurrentPage(1);
      }

      try {
        const result = mainData;

        if (!Array.isArray(result)) {
          console.warn(
            "API did not return an array of objects or returned empty data."
          );
          return;
        }

        valueCache.current.clear();

        if (loadedBackendConfig && loadedBackendConfig.columns) {
          const backendColumns = mapBackendColumns(loadedBackendConfig);

          setData(result);
          setColumns(backendColumns);
          setOriginalColumnOrder(backendColumns);

          if (loadedBackendConfig.sortConfig) {
            const nextSort = sortConfigFromUrl(
              searchParams,
              loadedBackendConfig.sortConfig
            );
            if (nextSort) setSortConfig(nextSort);
          }
          if (loadedBackendConfig.rowsPerPage) {
            setRowsPerPage(loadedBackendConfig.rowsPerPage);
          }
          if (loadedBackendConfig.filters) {
            // Handle filters - check if in FilterBuilder format (array) or simple format (object)
            // Note: We inline the logic here to avoid initialization order issues
            const configFilters = loadedBackendConfig.filters;

            if (Array.isArray(configFilters) && configFilters.length > 0) {
              // Filters are in FilterBuilder format, need to convert
              // This will be handled by the useEffect that watches loadedBackendConfig
              // For now, just store them - the useEffect at line 423 will convert them
              // We can't use convertFromFilterBuilderFormat here as it's defined later
              setPendingFilterBuilderFilters(configFilters);
            } else if (
              typeof configFilters === "object" &&
              !Array.isArray(configFilters)
            ) {
              // Already in simple format
              setFilters(configFilters);
              setPendingFilters(configFilters);
            }
          }
          return;
        }

        if (result.length === 0) {
          setData([]);
          setColumns([]);
          return;
        }

        const tableConfig = getTableConfig(configuration, tableConfigName);

        if (!tableConfig) {
          console.warn(`No configuration found for table: ${tableConfigName}`);
          setData(result);
          return;
        }

        const userConfig = getUserConfig(configuration, tableConfigName);
        const configToUse =
          userConfig || getDefaultConfig(configuration, tableConfigName);

        const configuredColumns = configToUse.columns
          .sort((a, b) => a.order - b.order)
          .map((configCol) => {
            const defaultCol = tableConfig.default_config.columns.find(
              (col) => col.key === configCol.key
            );
            return {
              key: configCol.key,
              label: defaultCol?.label || configCol.key,
              width: configCol.width || 150,
              visible:
                configCol.visible !== undefined ? configCol.visible : true,
              sortable:
                defaultCol?.sortable !== undefined ? defaultCol.sortable : true,
              filterable:
                defaultCol?.filterable !== undefined
                  ? defaultCol.filterable
                  : true,
              pinned: configCol.pinned || false,
            };
          });

        setData(result);
        setColumns(configuredColumns);
        setOriginalColumnOrder(configuredColumns);

        if (configToUse.sortConfig) {
          const nextSort = sortConfigFromUrl(
            searchParams,
            configToUse.sortConfig
          );
          if (nextSort) setSortConfig(nextSort);
        }
        if (configToUse.rowsPerPage) {
          setRowsPerPage(configToUse.rowsPerPage);
        }
        if (configToUse.filters) {
          setFilters(configToUse.filters);
        }
      } catch (error) {
        console.error("Failed to load data with config:", error);
        setSnackbar({
          open: true,
          message: `Failed to load data: ${error.message}`,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [getNestedValue, tableName, mainData, loadedBackendConfig, mapBackendColumns, paginationData, searchParams]
  );

  useEffect(() => {
    if (tableName) {
      loadDataFromConfiguration(tableName);
    }
  }, [loadDataFromConfiguration, tableName, loadedBackendConfig]);

  const parseDateTimeValue = (value) => {
    if (!value || typeof value !== "string") return null;

    const timestamp = Date.parse(value);
    if (!Number.isNaN(timestamp)) {
      return timestamp;
    }

    const dateTimeRegex =
      /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/;
    const match = value.match(dateTimeRegex);

    if (!match) return null;

    const [, dd, mm, yyyy, hh, min, ss] = match;
    const day = Number.parseInt(dd, 10);
    const month = Number.parseInt(mm, 10) - 1;
    const year = Number.parseInt(yyyy, 10);
    const hours = Number.parseInt(hh, 10);
    const minutes = Number.parseInt(min, 10);
    const seconds = Number.parseInt(ss || "0", 10);

    const parsed = new Date(year, month, day, hours, minutes, seconds).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  };

  const isServerPaginated = Boolean(paginationData?.totalPages || onSortChange);

  const sortedData = useMemo(() => {
    if (isServerPaginated) return data;

    const currentSort = sortConfig[0];
    if (!currentSort || !currentSort.key) return data;

    return [...data].sort((a, b) => {
      const aVal = getNestedValue(a, currentSort.key);
      const bVal = getNestedValue(b, currentSort.key);

      const parsedDateA = parseDateTimeValue(aVal);
      const parsedDateB = parseDateTimeValue(bVal);

      if (parsedDateA !== null && parsedDateB !== null) {
        return currentSort.direction === "asc"
          ? parsedDateA - parsedDateB
          : parsedDateB - parsedDateA;
      }

      const timeRegex =
        /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?(\s?(AM|PM))?$/i;
      if (
        typeof aVal === "string" &&
        typeof bVal === "string" &&
        timeRegex.test(aVal) &&
        timeRegex.test(bVal)
      ) {
        const parseTime = (timeStr) => {
          const [time, period] = timeStr.trim().split(" ");
          let [hours, minutes, seconds] = time.split(":").map(Number);
          if (isNaN(seconds)) seconds = 0;
          if (period?.toUpperCase() === "PM" && hours < 12) hours += 12;
          if (period?.toUpperCase() === "AM" && hours === 12) hours = 0;
          return hours * 3600 + minutes * 60 + seconds;
        };

        const timeA = parseTime(aVal);
        const timeB = parseTime(bVal);
        return currentSort.direction === "asc" ? timeA - timeB : timeB - timeA;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        if (shouldSortAsNumericString(currentSort.key)) {
          return compareNumericStringValues(aVal, bVal, currentSort.direction);
        }
        return currentSort.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return currentSort.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (aVal < bVal) return currentSort.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return currentSort.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, getNestedValue, isServerPaginated]);

  const filteredData = useMemo(() => {
    let result = sortedData;

    // Metadata / server-paginated lists: search is handled by the filtered API via URL ?search=
    if (!isServerPaginated && searchQuery.trim()) {
      const query = searchQuery?.toLowerCase().trim();
      result = result.filter((item) => {
        return (
          columns.some((col) => {
            const value = getNestedValue(item, col.key);
            if (value === null || value === undefined) return false;

            const stringValue = String(value)?.toLowerCase();
            const formattedLabel = formatLabel(col.key)?.toLowerCase();

            return (
              stringValue.includes(query) || formattedLabel.includes(query)
            );
          }) ||
          Object.values(item).some((value) => {
            if (value === null || value === undefined) return false;
            return String(value)?.toLowerCase().includes(query);
          })
        );
      });
    }

    Object.entries(filters).forEach(([key, value]) => {
      // Skip null/undefined values
      if (value === null || value === undefined) return;

      // Handle string values
      if (typeof value === "string") {
        if (value.trim() === "") return;

        const filterValue = value.toLowerCase().trim();
        result = result.filter((item) => {
          const itemValue = getNestedValue(item, key);
          if (itemValue === null || itemValue === undefined) return false;

          const itemString = String(itemValue)?.toLowerCase();
          const filterWords = filterValue
            ?.split(/\s+/)
            .filter((word) => word.length > 0);

          return filterWords.every((word) => itemString.includes(word));
        });
      }
      // Handle array values (for BETWEEN, IN, NOT_IN operators)
      else if (Array.isArray(value)) {
        // Skip empty arrays or arrays with all empty values
        const hasValidValues = value.some(
          (v) =>
            v !== null &&
            v !== undefined &&
            v !== "" &&
            !(typeof v === "string" && v.trim() === "")
        );
        if (!hasValidValues) return;

        // Note: Array-based filtering handled by backend/URL params
        // This section is for basic client-side filtering only
        // Advanced operators like BETWEEN are typically handled server-side
      }
    });

    return result;
  }, [sortedData, filters, searchQuery, columns, isServerPaginated, getNestedValue]);

  useEffect(() => {
    // Only validate page for client-side pagination
    // For server-side pagination, trust the backend response
    if (paginationData?.totalPages) {
      // Server-side pagination - backend handles validation
      return;
    }

    // Client-side pagination validation
    const totalPages = Math.max(
      1,
      Math.ceil(filteredData.length / rowsPerPage)
    );
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
      updateURL({ currentPage: totalPages });
    } else if (filteredData.length === 0 && currentPage !== 1) {
      setCurrentPage(1);
      updateURL({ currentPage: 1 });
    }
  }, [
    filteredData.length,
    rowsPerPage,
    currentPage,
    updateURL,
    paginationData,
  ]);

  const totalPages = useMemo(() => {
    if (paginationData?.totalPages) {
      return paginationData.totalPages;
    }
    const pages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
    return pages;
  }, [filteredData.length, rowsPerPage, paginationData]);

  const hasNext = paginationData?.hasNext ?? currentPage < totalPages;
  const hasPrev = paginationData?.hasPrev ?? currentPage > 1;

  const paginatedData = useMemo(() => {
    if (paginationData?.totalPages) {
      return data;
    }
    // Otherwise, paginate client-side
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [data, filteredData, currentPage, rowsPerPage, paginationData]);

  const handleSort = useCallback(
    (key) => {
      setSortConfig((prev) => {
        const currentSort = prev?.[0];
        const newDirection =
          currentSort?.key === key && currentSort.direction === "asc"
                ? "desc"
            : "asc";
        const newSortConfig = [{ key, direction: newDirection }];

        if (onSortChange) {
          onSortChange(key, newDirection);
        } else {
          updateURL({ sortConfig: newSortConfig, currentPage: 1 });
        }
        return newSortConfig;
      });
      setCurrentPage(1);
    },
    [updateURL, onSortChange]
  );

  const renderColumnSortIcon = useCallback(
    (col) => {
      if (col.sortable === false) return null;
      const isActive = sortConfig?.[0]?.key === col.key;
      const direction = sortConfig?.[0]?.direction;
      return (
        <Box
          className={`sort-icon${isActive ? " is-active" : ""}`}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            ml: 0.5,
            fontSize: "13px",
            lineHeight: 1,
            color: isActive ? "var(--ems-primary)" : "var(--ems-faint)",
            flexShrink: 0,
          }}
        >
          {isActive ? (direction === "asc" ? "↑" : "↓") : "⇅"}
        </Box>
      );
    },
    [sortConfig]
  );

  const handlePendingFilterChange = useCallback((key, value) => {
    setPendingFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const togglePendingColumnVisibility = useCallback((key) => {
    setPendingColumns((prev) =>
      prev.map((col) => {
        if (col.key === key && !col.mandatory) {
          return { ...col, visible: !col.visible };
        }
        return col;
      })
    );
  }, []);

  const handleSelectAllPendingColumns = useCallback((event) => {
    const isChecked = event.target.checked;
    setPendingColumns((prev) =>
      prev.map((col) => ({
        ...col,
        visible: col.mandatory ? true : isChecked,
      }))
    );
  }, []);

  const togglePendingColumnPinned = useCallback((key) => {
    setPendingColumns((prev) =>
      prev.map((col) => {
        if (col.key === key) {
          return { ...col, pinned: col.pinned === "left" ? undefined : "left" };
        }
        return col;
      })
    );
  }, []);

  const allPendingColumnsVisible = useMemo(() => {
    const nonMandatoryColumns = pendingColumns.filter((col) => !col.mandatory);
    return (
      nonMandatoryColumns.length > 0 &&
      nonMandatoryColumns.every((col) => col.visible)
    );
  }, [pendingColumns]);

  const buildColumnLayoutPayload = useCallback(
    (columnSnapshot) => ({
      columns: columnSnapshot.map((col, index) => ({
        key: col.key || col.field,
        field: col.field || col.key,
        label: col.label,
        width: col.width,
        visible: col.visible,
        pinned: col.pinned || undefined,
        order: index,
        mandatory: col.mandatory,
        sortable: col.sortable,
        filterable: col.filterable,
      })),
      sortConfig,
      rowsPerPage,
      filters: loadedBackendConfig?.filters || [],
      timestamp: new Date().toISOString(),
    }),
    [sortConfig, rowsPerPage, loadedBackendConfig?.filters]
  );

  const persistColumnLayout = useCallback(
    async (columnSnapshot) => {
      const org = userData?.organization;
      const orgId = org?.organization_id;
      if (!orgId || !datagridKey || !Array.isArray(columnSnapshot) || columnSnapshot.length === 0) {
        return;
      }

      try {
        const payload = {
          organization_id: orgId,
          organization_entity_id: org.organization_entity_id || null,
          datagrid_key: `${datagridKey}_${orgId}`,
          datagrid_default_configuration: buildColumnLayoutPayload(columnSnapshot),
        };

        const organizationUserId =
          userData?.organization_user_id ?? userData?.organization_user?.organization_user_id;
        if (organizationUserId) {
          payload.organization_user_id = organizationUserId;
        }

        const response = await fetch(`${MAIN_URL}/api/organization-datagrids`, {
          method: "POST",
          headers: getApiHeaders(),
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Save failed: ${response.status}`);
        }
      } catch (error) {
        console.error("Failed to persist column layout:", error);
      }
    },
    [userData, datagridKey, buildColumnLayoutPayload]
  );

  const scheduleColumnLayoutPersist = useCallback(
    (columnSnapshot) => {
      if (!columnLayoutReadyRef.current || !datagridKey) return;
      if (columnLayoutSaveTimerRef.current) {
        clearTimeout(columnLayoutSaveTimerRef.current);
      }
      columnLayoutSaveTimerRef.current = setTimeout(() => {
        persistColumnLayout(columnSnapshot);
      }, 700);
    },
    [datagridKey, persistColumnLayout]
  );

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  useEffect(() => {
    if (loadedBackendConfig?.columns?.length && columns.length > 0) {
      columnLayoutReadyRef.current = true;
    }
  }, [loadedBackendConfig, columns.length]);

  useEffect(() => {
    return () => {
      if (columnLayoutSaveTimerRef.current) {
        clearTimeout(columnLayoutSaveTimerRef.current);
      }
    };
  }, []);

  const handleColumnReorder = useCallback(
    (dragIndex, hoverIndex) => {
      const visibleCols = columns.filter((col) => col.visible);

      const dragColumn = visibleCols[dragIndex];
      const hoverColumn = visibleCols[hoverIndex];

      if (dragColumn?.pinned === "left" || hoverColumn?.pinned === "left") {
        return;
      }

      const dragColumnIndex = columns.findIndex(
        (col) => col.key === dragColumn.key
      );
      const hoverColumnIndex = columns.findIndex(
        (col) => col.key === hoverColumn.key
      );

      const newColumns = [...columns];
      newColumns.splice(dragColumnIndex, 1);
      newColumns.splice(hoverColumnIndex, 0, dragColumn);

      setColumns(newColumns);
      setOriginalColumnOrder(newColumns);
      scheduleColumnLayoutPersist(newColumns);
    },
    [columns, scheduleColumnLayoutPersist]
  );

  const handleMouseDown = useCallback(
    (e, key) => {
      e.stopPropagation();
      setResizingColumnKey(key);
      initialMouseX.current = e.clientX;
      const column = columns.find((col) => col.key === key);
      initialColumnWidth.current = column ? column.width : 0;
    },
    [columns]
  );

  useEffect(() => {
    if (!resizingColumnKey) return;
    const handleMouseMove = (e) => {
      if (resizingColumnKey) {
        const deltaX = e.clientX - initialMouseX.current;
        setColumns((prevColumns) =>
          prevColumns.map((col) =>
            col.key === resizingColumnKey
              ? {
                  ...col,
                  width: Math.max(50, initialColumnWidth.current + deltaX),
                }
              : col
          )
        );
      }
    };

    const handleMouseUp = () => {
      if (resizingColumnKey) {
        scheduleColumnLayoutPersist(columnsRef.current);
      }
      setResizingColumnKey(null);
      document.body.style.cursor = "default";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "default";
    };
  }, [resizingColumnKey, scheduleColumnLayoutPersist]);

  const applyFilters = useCallback(() => {
    // This is now handled by handleApplyFilterBuilderFilters
    // Keeping for backward compatibility if needed
    setFilters(pendingFilters);
    setCurrentPage(1);
    setShowFilters(false);
    updateURL({ filters: pendingFilters, currentPage: 1 });
  }, [pendingFilters, updateURL]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    setPendingFilters({});
    setPendingFilterBuilderFilters([]);
    setCurrentPage(1);
    updateURL({ filters: {}, currentPage: 1 });
  }, [updateURL]);

  const handleSaveView = useCallback(
    async (viewData) => {
      const org = userData?.organization;
      const applicationUserId = userData?.application_user_id;

      if (!org?.organization_id || !applicationUserId) {
        toast.error("User or organization information is missing");
        throw new Error("User or organization information is missing");
      }

      try {
        // Prepare view configuration
        const viewConfiguration = {
          filters: viewData.filters || [],
          sortConfig: viewData.sortConfig || sortConfig,
          columns:
            viewData.columns ||
            columns.map((col, index) => ({
              key: col.key || col.field,
              field: col.field || col.key,
              label: col.label,
              width: col.width,
              visible: col.visible,
              pinned: col.pinned,
              order: index,
            })),
          rowsPerPage: rowsPerPage,
        };

        // Prepare request payload
        const payload = {
          organization_id: org.organization_id,
          organization_entity_id: org.organization_entity_id || null,
          application_user_id: applicationUserId,
          datagrid_key: viewData.datagridKey || tableName || "default",
          view_name: viewData.viewName,
          view_configuration: viewConfiguration,
          is_default: false, // You can add a checkbox in the modal if needed
        };

        const response = await fetch(
          `${MAIN_URL}/api/organization-datagrid-user-views`,
          {
            method: "POST",
            headers: getApiHeaders(),
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || `HTTP error! status: ${response.status}`
          );
        }

        const result = await response.json();
        toast.success("View saved successfully!");

        // Trigger refresh of saved views
        setViewSavedTrigger((prev) => prev + 1);

        return result;
      } catch (error) {
        console.error("Error saving view:", error);
        toast.error(error.message || "Failed to save view");
        throw error;
      }
    },
    [userData, sortConfig, columns, rowsPerPage, tableName]
  );

  // Helper to parse date string to Date object
  const parseDateValue = useCallback((dateString) => {
    if (!dateString || typeof dateString !== "string") return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return null;
      }
      return date;
    } catch (e) {
      return null;
    }
  }, []);

  // Helper to check if a field is likely a date field based on field name
  const isDateField = useCallback((fieldName) => {
    const lowerFieldName = fieldName.toLowerCase();
    return (
      lowerFieldName.includes("date") ||
      lowerFieldName === "date_of_birth" ||
      lowerFieldName === "date_of_joining" ||
      lowerFieldName === "date_of_birth" ||
      lowerFieldName === "joining_date" ||
      lowerFieldName === "dob"
    );
  }, []);

  // Convert simple filters {key: value} to FilterBuilder format [{field, operator, value}]
  const convertToFilterBuilderFormat = useCallback(
    (simpleFilters, searchParamsForOperators = null) => {
      const paramsToUse = searchParamsForOperators || searchParams;

      return Object.entries(simpleFilters)
        .filter(([key, value]) => {
          // Read operator from URL params first to check if it requires a value
          const operatorKey = `filter_${key}_operator`;
          const operator = paramsToUse.get(operatorKey) || OPERATORS.CONTAINS;
          const requiresValue = operatorRequiresValue(operator);

          // Include filter if:
          // 1. Operator doesn't require value (today, this_week, this_month, is_null, is_not_null)
          // 2. Or value is valid (not null, undefined, or empty string)
          return (
            !requiresValue ||
            (value !== null && value !== undefined && value !== "")
          );
        })
        .map(([key, value], index) => {
          // Read operator from URL params
          const operatorKey = `filter_${key}_operator`;
          let operator = paramsToUse.get(operatorKey) || OPERATORS.CONTAINS; // Default to contains
          const requiresValue = operatorRequiresValue(operator);

          // For operators that don't require values, set value to null
          if (!requiresValue) {
            return {
              field: key,
              operator: operator,
              value: null,
            };
          }

          // Check if operator requires multiple values (array)
          const requiresMultipleValues =
            operatorRequiresMultipleValues(operator);

          // Parse date values to Date objects for DatePicker
          let parsedValue = value;

          // Handle array values (for BETWEEN, IN, NOT_IN operators)
          if (requiresMultipleValues && typeof value === "string") {
            // Split comma-separated string back to array
            const arrayValues = value
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v !== "");

            if (isDateField(key) && operator === OPERATORS.BETWEEN) {
              // Parse date array for BETWEEN operator
              parsedValue = arrayValues.map((dateStr) => {
                const dateValue = parseDateValue(dateStr);
                return dateValue || dateStr; // Return Date object or original string if parsing fails
              });
            } else {
              // For IN, NOT_IN operators, keep as string array
              parsedValue = arrayValues;
            }
          } else if (
            (operator === OPERATORS.EQUALS || operator === OPERATORS.NOT_EQUALS) &&
            typeof value === 'string' &&
            value.includes(',')
          ) {
            // Multi-select FK/enum: URL stores ids as "91,2,57"
            const idParts = splitCommaSeparatedFilterValue(value);
            if (idParts.length > 1) {
              parsedValue = idParts;
            }
          } else if (isDateField(key) && !requiresMultipleValues) {
            // Single date value
            const dateValue = parseDateValue(String(value));
            if (dateValue) {
              parsedValue = dateValue;
              // If no operator specified for date, default to 'on'
              if (!paramsToUse.get(operatorKey)) {
                operator = OPERATORS.ON;
              }
            }
          } else if (typeof value === "string" && value.trim() !== "") {
            parsedValue = value.trim();
          }

          const labelKey = `filter_${key}_label`;
          const labelParam = paramsToUse.get(labelKey);
          let valueLabel;
          if (labelParam) {
            const labelParts = labelParam
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            valueLabel = labelParts.length > 1 ? labelParts : labelParam;
          }

          return {
            field: key,
            operator: operator,
            value: parsedValue,
            ...(valueLabel !== undefined && valueLabel !== null && { valueLabel }),
          };
        });
    },
    [searchParams, isDateField, parseDateValue]
  );

  // Helper function to format date to YYYY-MM-DD without timezone issues
  const formatDateForFilter = (date) => {
    if (!(date instanceof Date)) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Convert FilterBuilder format [{field, operator, value}] to simple filters {key: value}
  // Returns object with filters and operators
  const convertFromFilterBuilderFormat = useCallback((filterBuilderFilters) => {
    const simpleFilters = {};
    const filterOperators = {};
    const filterLabels = {};

    filterBuilderFilters.forEach((filter, index) => {
      if (!filter.field || !filter.operator) return;

      // Check if operator requires a value
      const requiresValue = operatorRequiresValue(filter.operator);

      // For operators that don't require values (today, this_week, this_month, is_null, is_not_null)
      // Allow null values and still include the filter
      if (!requiresValue) {
        // Store operator even if value is null
        filterOperators[filter.field] = filter.operator;
        // Store a special marker or empty string for URL compatibility
        simpleFilters[filter.field] = "";
        return;
      }

      // For operators that require values, check if value is valid (reject undefined FK ids)
      const cleanedValue = Array.isArray(filter.value)
        ? filter.value.filter(
            (v) =>
              v != null &&
              v !== "" &&
              v !== "undefined" &&
              String(v).trim() !== ""
          )
        : filter.value;
      const isValidValue =
        cleanedValue !== null &&
        cleanedValue !== undefined &&
        cleanedValue !== "" &&
        cleanedValue !== "undefined" &&
        !(Array.isArray(cleanedValue) && cleanedValue.length === 0) &&
        String(cleanedValue) !== "undefined";

      if (isValidValue) {
        // Convert date objects to strings for URL storage
        let valueToStore = cleanedValue;
        if (filter.value instanceof Date) {
          // Use YYYY-MM-DD format to avoid timezone issues
          valueToStore = formatDateForFilter(filter.value);
        } else if (
          Array.isArray(filter.value) &&
          filter.value.length > 0 &&
          filter.value[0] instanceof Date
        ) {
          // Handle date arrays (for BETWEEN operator)
          valueToStore = filter.value
            .map((d) => (d instanceof Date ? formatDateForFilter(d) : d))
            .join(",");
        } else if (Array.isArray(filter.value)) {
          if (
            filter.operator === OPERATORS.EQUALS ||
            filter.operator === OPERATORS.NOT_EQUALS
          ) {
            valueToStore = filter.value.map((v) => String(v)).join("|");
          } else {
            valueToStore = filter.value.map((v) => String(v)).join(",");
          }
        } else {
          valueToStore = String(filter.value);
        }

        simpleFilters[filter.field] = valueToStore;

        if (filter.valueLabel != null && filter.valueLabel !== '') {
          filterLabels[filter.field] = Array.isArray(filter.valueLabel)
            ? filter.valueLabel.join(',')
            : String(filter.valueLabel);
        }

        // Store operator if it's not the default (contains)
        // Also store operators for date fields and other special cases
        if (filter.operator && filter.operator !== OPERATORS.CONTAINS) {
          filterOperators[filter.field] = filter.operator;
        } else if (filter.operator) {
          // Store all operators, not just non-default ones
          filterOperators[filter.field] = filter.operator;
        }
      }
    });

    return {
      filters: simpleFilters,
      operators: filterOperators,
      labels: filterLabels,
    };
  }, []);

  // Add function to load a saved view
  const handleLoadSavedView = useCallback(
    (view) => {
      if (!view?.view_configuration) {
        toast.error("Invalid view configuration");
        return;
      }

      const config = view.view_configuration;

      // Prepare URL updates object to combine all updates
      const urlUpdates = {
        currentPage: 1,
      };

      // Load filters
      if (
        config.filters &&
        Array.isArray(config.filters) &&
        config.filters.length > 0
      ) {
        setPendingFilterBuilderFilters(config.filters);
        // Convert to simple format and apply
        const { filters: simpleFilters, operators: filterOperators, labels: filterLabels } =
          convertFromFilterBuilderFormat(config.filters);
        setFilters(simpleFilters);
        setPendingFilters(simpleFilters);

        urlUpdates.filters = simpleFilters;
        urlUpdates.filterOperators = filterOperators;
        urlUpdates.filterLabels = filterLabels;

        // Notify parent with FilterBuilder format (for API calls if needed)
        // Ensure filters have value: null for operators that don't require values
        const filtersForAPI = config.filters.map((filter) => {
          const requiresValue = operatorRequiresValue(filter.operator);
          return {
            field: filter.field,
            operator: filter.operator,
            value: requiresValue
              ? filter.value !== undefined
                ? filter.value
                : null
              : null,
          };
        });
        onFilterChange?.(filtersForAPI);
      } else {
        // If no filters, clear them
        urlUpdates.filters = {};
        urlUpdates.filterOperators = {};
        onFilterChange?.([]);
      }

      // Load sort configuration
      if (
        config.sortConfig &&
        Array.isArray(config.sortConfig) &&
        config.sortConfig.length > 0
      ) {
        setSortConfig(config.sortConfig);
        urlUpdates.sortConfig = config.sortConfig;
      }

      // Load rows per page
      if (config.rowsPerPage) {
        setRowsPerPage(config.rowsPerPage);
        urlUpdates.rowsPerPage = config.rowsPerPage;
      }

      // Update URL with all changes at once
      updateURL(urlUpdates);

      // Load columns (this doesn't affect URL, so it's separate)
      if (
        config.columns &&
        Array.isArray(config.columns) &&
        config.columns.length > 0
      ) {
        // Map saved columns back to column format
        const loadedColumns = config.columns
          .map((savedCol) => ({
            key: savedCol.key || savedCol.field,
            field: savedCol.field || savedCol.key,
            label: savedCol.label,
            width: savedCol.width || 150,
            visible: savedCol.visible !== undefined ? savedCol.visible : true,
            pinned: savedCol.pinned || false,
            order: savedCol.order || 0,
          }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        setColumns(loadedColumns);
      }

      setCurrentPage(1);
      toast.success(`View "${view.view_name}" loaded successfully!`);
    },
    [convertFromFilterBuilderFormat, updateURL, onFilterChange]
  );

  // Helper function to load filters from loadedBackendConfig
  // Handles both FilterBuilder format (array) and simple format (object)
  const loadFiltersFromConfig = useCallback(
    (configFilters) => {
      if (!configFilters) return;

      // Check if filters are in FilterBuilder format (array)
      if (Array.isArray(configFilters) && configFilters.length > 0) {
        // Convert FilterBuilder format to simple format
        const { filters: simpleFilters, operators: filterOperators, labels: filterLabels } =
          convertFromFilterBuilderFormat(configFilters);

        setFilters(simpleFilters);
        setPendingFilters(simpleFilters);

        // Update URL params with the converted filters
        updateURL({
          filters: simpleFilters,
          filterOperators: filterOperators,
          filterLabels: filterLabels,
        });

        if (process.env.NODE_ENV === "development") {
          console.debug("Loaded filters from FilterBuilder format:", {
            filterBuilder: configFilters,
            simple: simpleFilters,
            operators: filterOperators,
          });
        }
      } else if (
        typeof configFilters === "object" &&
        !Array.isArray(configFilters)
      ) {
        // Already in simple format (object)
        setFilters(configFilters);
        setPendingFilters(configFilters);
        if (process.env.NODE_ENV === "development") {
          console.debug("Loaded filters from simple format:", configFilters);
        }
      }
    },
    [convertFromFilterBuilderFormat, updateURL]
  );

  // Load filters from loadedBackendConfig
  // This useEffect is placed after loadFiltersFromConfig is defined to avoid initialization order issues
  useEffect(() => {
    // Process filters if they exist (handles both FilterBuilder format and simple format)
    if (loadedBackendConfig?.filters) {
      // Now loadFiltersFromConfig is available since we're after its definition
      // It handles both FilterBuilder format (array) and simple format (object)
      loadFiltersFromConfig(loadedBackendConfig.filters);
    }
  }, [loadedBackendConfig?.filters, loadFiltersFromConfig]);

  const handleFilterDialogOpen = useCallback(() => {
    // Sync pending filters with current active filters from state
    setPendingFilters(filters);

    // Convert current filters to FilterBuilder format so they show in the dialog
    // Pass searchParams to read operators from URL
    const filterBuilderFilters = convertToFilterBuilderFormat(
      filters,
      searchParams
    );
    setPendingFilterBuilderFilters(filterBuilderFilters);

    // Open the dialog
    setShowFilters(true);
  }, [filters, convertToFilterBuilderFormat, searchParams]);

  const handleFilterDialogClose = useCallback(() => {
    setPendingFilters(filters);
    const filterBuilderFilters = convertToFilterBuilderFormat(filters);
    setPendingFilterBuilderFilters(filterBuilderFilters);
    setShowFilters(false);
  }, [filters, convertToFilterBuilderFormat]);

  const handleFilterBuilderChange = useCallback((newFilters) => {
    setPendingFilterBuilderFilters(newFilters);
  }, []);

  // Convert filters from URL to FilterBuilder format and notify parent when filters or searchParams change
  // This must be after convertToFilterBuilderFormat is defined
  useEffect(() => {
    // Wait for initialization to complete
    if (!hasInitializedFromURL.current) return;

    // Read current filters and operators from URL params (in case they changed externally)
    const currentFilters = {};
    const currentOperators = {};
    searchParams.forEach((value, key) => {
      if (
        key.startsWith("filter_") &&
        !key.endsWith("_operator") &&
        !key.endsWith("_label")
      ) {
        const filterKey = key.replace("filter_", "");
        const operatorKey = `filter_${filterKey}_operator`;
        const operator = searchParams.get(operatorKey);
        const requiresValue = operator ? operatorRequiresValue(operator) : true;

        if (value || !requiresValue) {
          currentFilters[filterKey] = value || "";
          if (operator) {
            currentOperators[filterKey] = operator;
          }
        }
      }
    });

    // Update local filters state if URL changed
    if (JSON.stringify(currentFilters) !== JSON.stringify(filters)) {
      setFilters(currentFilters);
    }

    // Only proceed if we have filters to convert
    if (
      Object.keys(currentFilters).length === 0 &&
      Object.keys(currentOperators).length === 0
    ) {
      // No filters, notify parent with empty array
      onFilterChange?.([]);
      return;
    }

    // Convert simple filters from URL to FilterBuilder format
    const filterBuilderFilters = convertToFilterBuilderFormat(currentFilters);

    // Normalize filters for API - ensure value: null for operators that don't require values
    const filtersForAPI = filterBuilderFilters.map((filter) => {
      const requiresValue = operatorRequiresValue(filter.operator);
      return {
        field: filter.field,
        operator: filter.operator,
        value: requiresValue
          ? filter.value !== undefined
            ? filter.value
            : null
          : null,
      };
    });

    // Notify parent component with FilterBuilder format filters
    onFilterChange?.(filtersForAPI);
  }, [searchParams, convertToFilterBuilderFormat, onFilterChange, filters]);

  const handleApplyFilterBuilderFilters = useCallback(() => {
    const betweenOperators = new Set([
      OPERATORS.BETWEEN,
      OPERATORS.NOT_BETWEEN,
      OPERATORS.DATE_BETWEEN,
      OPERATORS.DATE_NOT_BETWEEN,
    ]);

    for (const filter of pendingFilterBuilderFilters) {
      if (!betweenOperators.has(filter.operator)) continue;

      const [from, to] = Array.isArray(filter.value) ? filter.value : [null, null];

      const missingFrom = from === null || from === '' || from === undefined;
      const missingTo = to === null || to === '' || to === undefined;

      if (missingFrom && missingTo) {
        toast.error('"From" and "To" values are required when using the Between operator.');
        return;
      }
      if (missingFrom) {
        toast.error('"From" value is required when using the Between operator.');
        return;
      }
      if (missingTo) {
        toast.error('"To" value is required when using the Between operator.');
        return;
      }

      if (
        (filter.operator === OPERATORS.BETWEEN || filter.operator === OPERATORS.NOT_BETWEEN) &&
        Number(from) > Number(to)
      ) {
        toast.error('"From" value must be less than or equal to "To" value.');
        return;
      }

      if (
        (filter.operator === OPERATORS.DATE_BETWEEN || filter.operator === OPERATORS.DATE_NOT_BETWEEN) &&
        new Date(from) > new Date(to)
      ) {
        toast.error('"From" date must be before or equal to "To" date.');
        return;
      }
    }

    // Convert FilterBuilder filters back to simple format for URL storage
    const { filters: simpleFilters, operators: filterOperators, labels: filterLabels } =
      convertFromFilterBuilderFormat(pendingFilterBuilderFilters);
    setFilters(simpleFilters);
    setPendingFilters(simpleFilters);
    setCurrentPage(1);
    setShowFilters(false);
    updateURL({
      filters: simpleFilters,
      filterOperators: filterOperators,
      filterLabels: filterLabels,
      currentPage: 1,
    });

    // Also notify parent with FilterBuilder format (for API calls if needed)
    // Ensure filters have value: null for operators that don't require values
    const filtersForAPI = pendingFilterBuilderFilters.map((filter) => {
      const requiresValue = operatorRequiresValue(filter.operator);
      return {
        field: filter.field,
        operator: filter.operator,
        value: requiresValue
          ? filter.value !== undefined
            ? filter.value
            : null
          : null,
      };
    });
    onFilterChange?.(filtersForAPI);
  }, [
    pendingFilterBuilderFilters,
    convertFromFilterBuilderFormat,
    updateURL,
    onFilterChange,
  ]);

  const handleColumnDialogOpen = useCallback(() => {
    const columnsWithCurrentVisibility = originalColumnOrder.map((origCol) => {
      const currentCol = columns.find((col) => col.key === origCol.key);
      return {
        ...origCol,
        visible: currentCol?.visible ?? origCol.visible,
        mandatory: origCol.mandatory ?? false,
      };
    });
    setPendingColumns(columnsWithCurrentVisibility);
    setColumnManagerOpen(true);
  }, [originalColumnOrder, columns]);

  const handleColumnDialogClose = useCallback(() => {
    setPendingColumns([...columns]);
    setColumnManagerOpen(false);
  }, [columns]);

  const applyColumnChanges = useCallback(() => {
    const nextColumns = [...pendingColumns];
    setColumns(nextColumns);
    setOriginalColumnOrder(nextColumns);
    scheduleColumnLayoutPersist(nextColumns);
    setColumnManagerOpen(false);
  }, [pendingColumns, scheduleColumnLayoutPersist]);

  const cancelColumnChanges = useCallback(() => {
    setPendingColumns([...columns]);
    setColumnManagerOpen(false);
  }, [columns]);

  const visibleColumns = useMemo(() => {
    const visible = columns.filter((col) => col.visible);
    return visible.sort((a, b) => {
      if (a.pinned === "left" && b.pinned !== "left") return -1;
      if (a.pinned !== "left" && b.pinned === "left") return 1;
      return 0;
    });
  }, [columns]);

  const activeFilters = useMemo(
    () => Object.keys(filters).filter((key) => filters[key]?.length),
    [filters]
  );

  const handleEdit = useCallback(
    (item) => {
      if (EditFunc) {
        EditFunc(item);
      } else {
        navigate(`${Route}/edit/${item?.[mainKey]}`);
      }
    },
    [EditFunc, navigate, Route, mainKey]
  );

  const handleShowData = useCallback(
    (item) => {
      if (viewFunction) {
        viewFunction(item);
      } else if (handleShow) {
        handleShow(item);
      } else {
        navigate(`${Route}/view/${item?.[mainKey]}`);
      }
    },
    [viewFunction, handleShow, navigate, Route, mainKey]
  );

  const renderInfoAction = useCallback(
    (item, stopPropagation = false) => {
      if (infoFunction == undefined || !hasInfoPermission) return null;

      return (
        <Tooltip title="Info">
          <IconButton
            size="small"
            onClick={(e) => {
              if (stopPropagation) e.stopPropagation();
              infoFunction(item);
            }}
            sx={{
              color: theme.palette.mode === "dark" ? "#fff" : "#333",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.1)"
                    : "#33333315",
              },
            }}
          >
            <Info size={16} />
          </IconButton>
        </Tooltip>
      );
    },
    [infoFunction, hasInfoPermission, theme.palette.mode]
  );

  const hasActionColumn = useMemo(() => {
    if (showActions != null) return Boolean(showActions);
    return (
      showReceiptButton ||
      showFeePortalButton ||
      showBatchAttendanceButton ||
      showBatchClassesButton ||
      showBatchStudentsButton ||
      showBatchRecordsButton ||
      showEnrolledButton ||
      showSnapshotButton ||
      showLeadActivityButton ||
      showAdmissionButton ||
      showViewButton ||
      showDemoParticipantButton ||
      showEditButton ||
      showDeleteButton
    );
  }, [
    showActions,
    showReceiptButton,
    showFeePortalButton,
    showBatchAttendanceButton,
    showBatchClassesButton,
    showBatchStudentsButton,
    showBatchRecordsButton,
    showEnrolledButton,
    showSnapshotButton,
    showLeadActivityButton,
    showAdmissionButton,
    showViewButton,
    showDemoParticipantButton,
    showEditButton,
    showDeleteButton,
  ]);

  const actionButtonsCount = [
    showReceiptButton,
    showFeePortalButton,
    showBatchAttendanceButton,
    showBatchClassesButton,
    showBatchStudentsButton,
    showBatchRecordsButton,
    showEnrolledButton,
    showSnapshotButton,
    showLeadActivityButton,
    showAdmissionButton,
    showViewButton,
    showDemoParticipantButton,
    showEditButton,
    showDeleteButton,
  ].filter(Boolean).length;

  const actionColumnWidth = Math.max(
    toggleActiveFn ? 140 : 120,
    (actionButtonsCount + (toggleActiveFn ? 1 : 0)) * 34 + 20
  );

  const actionBtnSx = useMemo(
    () => ({
      color: theme.palette.mode === "dark" ? "#fff" : "#333",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.1)"
            : "#33333315",
      },
    }),
    [theme.palette.mode]
  );

  const handleDelete = useCallback((item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    const recordId = selectedItem?.[mainKey] ?? selectedItem?.id;
    try {
      if (DeleteFunc) {
        await DeleteFunc(recordId ?? selectedItem);
      } else {
        setData((prevData) =>
          prevData.filter(
            (item) => (item?.[mainKey] ?? item?.id) !== recordId
          )
        );
        toast.success("Deleted successfully!");
      }
    } catch (error) {
      console.error("Delete Error:", error);
      if (error?.code !== "EMS_CASCADE_REQUIRED") {
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Failed to delete. Please try again."
        );
      }
    } finally {
      setDeleteDialogOpen(false);
      setSelectedItem(null);
    }
  }, [selectedItem, DeleteFunc, mainKey]);

  const massCopyPath =
    Route === "/project-task" && tableName === "organization_project_tasks"
      ? `${Route}/MassCopy`
      : null;

  const handleLeadActivity = useCallback(
    (item) => {
      if (!LeadActivityFunc) return;

      if (leadsTable) {
        const tz = String(item?.__lct_timezone || "").trim();
        const mStart = item?.__lct_morning_start;
        const mEnd = item?.__lct_morning_end;
        const eStart = item?.__lct_evening_start;
        const eEnd = item?.__lct_evening_end;

        const activeNow = isWithinLeadContactWindows(
          tz,
          mStart,
          mEnd,
          eStart,
          eEnd
        );

        if (!activeNow) {
          setPendingLeadActivityItem(item);
          setLeadActivityConfirmOpen(true);
          return;
        }
      }

      LeadActivityFunc(item);
    },
    [LeadActivityFunc, leadsTable]
  );

  const renderRowActions = useCallback(
    (row, { stopPropagation = false } = {}) => {
      const onAction = (e, fn) => {
        if (stopPropagation && e?.stopPropagation) e.stopPropagation();
        fn?.(row);
      };

      return (
        <>
          {renderInfoAction(row, stopPropagation)}
          {showReceiptButton && (
            <Tooltip title="Receipt">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, ReceiptFunc)}
                sx={actionBtnSx}
              >
                <Printer size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showFeePortalButton && (
            <Tooltip title="Fee portal">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, FeePortalFunc)}
                sx={actionBtnSx}
              >
                <Wallet size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showBatchAttendanceButton && (
            <Tooltip title="Class attendance marking">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, BatchAttendanceFunc)}
                sx={actionBtnSx}
              >
                <ClipboardCheck size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showBatchClassesButton && (
            <Tooltip title="Batch classes">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, BatchClassesFunc)}
                sx={actionBtnSx}
              >
                <ListChecks size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showBatchStudentsButton && (
            <Tooltip title="Batch students">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, BatchStudentsFunc)}
                sx={actionBtnSx}
              >
                <Users size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showBatchRecordsButton && (
            <Tooltip title="Batch records">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, BatchRecordsFunc)}
                sx={actionBtnSx}
              >
                <ClipboardCheck size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showEnrolledButton && (
            <Tooltip title="Enrolled">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, EnrolledFunc)}
                sx={actionBtnSx}
              >
                <User size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showSnapshotButton && (
            <Tooltip title="Snapshot">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, SnapshotFunc)}
                sx={actionBtnSx}
              >
                <Calendar size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showLeadActivityButton && (
            <Tooltip title="Lead activity">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, handleLeadActivity)}
                sx={actionBtnSx}
              >
                <ListChecks size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showAdmissionButton && (
            <Tooltip title="Admission">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, AdmissionFunc)}
                sx={actionBtnSx}
              >
                <Calendar size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showViewButton && (
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, handleShowData)}
                sx={actionBtnSx}
              >
                <View size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showDemoParticipantButton && (
            <Tooltip title="Demo Participant">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, DemoParticipantFunc)}
                sx={actionBtnSx}
              >
                <Users size={16} />
              </IconButton>
            </Tooltip>
          )}
          {typeof copyFunction === "function" && (
            <Tooltip title="Copy">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, copyFunction)}
                sx={actionBtnSx}
              >
                <Copy size={16} />
              </IconButton>
            </Tooltip>
          )}
          {showEditButton && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, handleEdit)}
                sx={actionBtnSx}
              >
                <Edit size={16} />
              </IconButton>
            </Tooltip>
          )}
          {massCopyPath ? (
            <Tooltip title="Mass Copy">
              <IconButton
                size="small"
                onClick={(e) => {
                  if (stopPropagation && e?.stopPropagation) e.stopPropagation();
                  navigate(massCopyPath);
                }}
                sx={actionBtnSx}
              >
                <Copy size={16} />
              </IconButton>
            </Tooltip>
          ) : null}
          {showDeleteButton && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={(e) => onAction(e, handleDelete)}
                sx={actionBtnSx}
              >
                <Trash2 size={16} />
              </IconButton>
            </Tooltip>
          )}
        </>
      );
    },
    [
      renderInfoAction,
      showReceiptButton,
      showFeePortalButton,
      showBatchAttendanceButton,
      showBatchClassesButton,
      showBatchStudentsButton,
      showBatchRecordsButton,
      showEnrolledButton,
      showSnapshotButton,
      showLeadActivityButton,
      showAdmissionButton,
      showViewButton,
      showDemoParticipantButton,
      showEditButton,
      showDeleteButton,
      ReceiptFunc,
      FeePortalFunc,
      BatchAttendanceFunc,
      BatchClassesFunc,
      BatchStudentsFunc,
      BatchRecordsFunc,
      EnrolledFunc,
      SnapshotFunc,
      handleLeadActivity,
      AdmissionFunc,
      DemoParticipantFunc,
      actionBtnSx,
      copyFunction,
      EditFunc,
      handleEdit,
      massCopyPath,
      navigate,
      DeleteFunc,
      handleDelete,
      handleShowData,
    ]
  );

  const wrapActionCellWithActiveToggle = useCallback(
    (row, iconRowJsx) => {
      if (!toggleActiveFn) {
        return iconRowJsx;
      }
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.25,
            flexWrap: "nowrap",
          }}
        >
          <Tooltip title={getToggleActiveTooltip(row)}>
            <Box
              component="span"
              onClick={(e) => e.stopPropagation()}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Switch
                size="small"
                checked={getRowActiveForToggle(row)}
                disabled={isToggleActiveDisabled(row)}
                onChange={(e) => {
                  e.stopPropagation();
                  if (isToggleActiveDisabled(row)) return;
                  toggleActiveFn(row, e.target.checked);
                }}
                inputProps={{ "aria-label": "Active" }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#81c784",
                    "&:hover": {
                      backgroundColor: "rgba(129, 199, 132, 0.14)",
                    },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#c8e6c9",
                    opacity: 1,
                  },
                }}
              />
            </Box>
          </Tooltip>
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {iconRowJsx}
          </Box>
        </Box>
      );
    },
    [toggleActiveFn]
  );

  const DynamicUserCard = ({ item, index }) => {
    const visibleCols = visibleColumns;
    return (
      <Card
        sx={{
          mb: 2,
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          "&:hover": {
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          },
          transition: "box-shadow 0.2s ease-in-out",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {visibleCols
                .filter((col) => col.key)
                .map((col) => {
                  const cellContent = renderDataCellContent(item, col);

                  return (
                    <Box
                      key={col.key}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        py: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          color: colors.text.secondary,
                          mt: 0.25,
                          minWidth: 20,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      ></Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            display: "block",
                            mb: 0.25,
                          }}
                        >
                          {col.label}
                        </Typography>
                        <Box
                          sx={{
                            color: colors.text.primary,
                            fontSize: "0.875rem",
                            wordBreak: "break-word",
                            lineHeight: 1.4,
                          }}
                        >
                          {cellContent}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
            </Box>

            {hasActionColumn && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  {renderRowActions(item)}
                </Box>
                <Chip
                  label={`#${(currentPage - 1) * rowsPerPage + index + 1}`}
                  size="small"
                  sx={{
                    backgroundColor: colors.grey[200],
                    color: colors.text.secondary,
                    fontSize: "0.7rem",
                    height: 20,
                  }}
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  return (
    <Box
      sx={{
        pt: 0,
        px: isSmallScreen ? 0 : 0,
        pb: isSmallScreen ? 2 : 3,
        maxWidth: "100%",
        margin: "0 auto",
        fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif',
        backgroundColor: colors.background,
        minHeight: "100vh",
      }}
    >
      <Dialog
        open={leadActivityConfirmOpen}
        onClose={() => {
          setLeadActivityConfirmOpen(false);
          setPendingLeadActivityItem(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Outside contact hours</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This lead is currently outside their preferred contact time window.
          </Typography>
          <Typography variant="body2">
            Is this a right time to call{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {String(pendingLeadActivityItem?.person_full_name || "this lead")}
            </Box>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLeadActivityConfirmOpen(false);
              setPendingLeadActivityItem(null);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              const item = pendingLeadActivityItem;
              setLeadActivityConfirmOpen(false);
              setPendingLeadActivityItem(null);
              if (LeadActivityFunc && item) {
                LeadActivityFunc(item);
              }
            }}
            variant="contained"
            color="primary"
          >
            Yes, proceed
          </Button>
        </DialogActions>
      </Dialog>

      {!hideToolbar && (
        <Toolbar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          view={view}
          setView={setView}
          colors={colors}
          isSmallScreen={isSmallScreen}
          // Export dependencies
          columns={columns}
          filteredData={filteredData}
          getNestedValue={getNestedValue}
          formatLabel={formatLabel}
          userData={userData}
          tableName={tableName}
          exportTitle={exportTitle || tableName}
          recordKey={recordKey}
          recordLabel={recordLabel}
          // Search dependencies
          updateURL={updateURL}
          setCurrentPage={setCurrentPage}
          // Filter dependencies
          loadedBackendConfig={loadedBackendConfig}
          convertToFilterBuilderFormat={convertToFilterBuilderFormat}
          setPendingFilters={setPendingFilters}
          setPendingFilterBuilderFilters={setPendingFilterBuilderFilters}
          setLockedFilters={setLockedFilters}
          setShowFilters={setShowFilters}
          handleFilterDialogOpen={handleFilterDialogOpen}
          // Print dependencies
          data={data}
          searchPlaceholder={searchPlaceholder}
          hideViewToggle={[
            "ems_lead_contact_timings",
            "ems_demo_sessions",
            "ems_demo_participants",
            "ems_assessments",
            "ems_assessment_results",
            "ems_certificates",
            "ems_recruitment_agencies",
            "ems_companies",
            "ems_placement_referrals",
            "ems_student_review_platforms",
            "ems_student_reviews",
            "ems_lead_stages",
            "ems_lead_sources",
            "ems_training_program_categories",
            "ems_training_programs",
            "ems_training_spaces",
            "Users",
            "organization_roles",
          ].includes(tableName)}
          hideFilterButton={hideFilterButton}
          hidePersonalizeView={hidePersonalizeView}
        />
      )}

      {/* Active Filters Bar */}
      {!hideActiveFiltersBar && (
      <ActiveFiltersBar
        configss={configss}
        module={filterModule || tableName}
        apiResolvedFilterLabels={apiResolvedFilterLabels}
      />
      )}

      {/* Saved Views Bar */}
      <SavedViewsBar
        datagridKey={tableName}
        onLoadView={handleLoadSavedView}
        onViewSaved={viewSavedTrigger}
      />

      {showFilters && (
        <div className="ems-flt-backdrop" onClick={handleFilterDialogClose}>
          <div
            className="ems-flt-modal"
            role="dialog"
            aria-label="Filters"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ems-flt-head">
              <div className="ems-flt-head-title">
                <Filter size={18} />
                Filters
                {pendingFilterBuilderFilters.length > 0 && (
                  <span className="ems-flt-head-count">
                    {pendingFilterBuilderFilters.length}
                  </span>
                )}
              </div>
              <button
                className="ems-icon-btn"
                title="Close"
                aria-label="Close"
                onClick={handleFilterDialogClose}
              >
                <X size={20} />
              </button>
            </div>

            {filterModule || tableName ? (
              <FilterBuilder
                key={`filter-builder-${tableName || filterModule || "default"}`} // Unique key per table
                module={filterModule || tableName}
                onFiltersChange={handleFilterBuilderChange}
                onApplyFilters={handleApplyFilterBuilderFilters}
                onClearFilters={clearAllFilters}
                showApplyButton={true}
                showClearButton={true}
                initialFilters={pendingFilterBuilderFilters}
                onSaveView={handleSaveView}
                columns={columns}
                sortConfig={sortConfig}
                datagridKey={tableName}
              />
            ) : (
              <>
                <div className="ems-flt-body">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {visibleColumns
                      .filter((col) => col.filterable !== false)
                      .map((col) => {
                        const isLocked = lockedFilters[col.key];

                        return (
                          <TextField
                            key={col.key}
                            label={formatLabel(col.label)}
                            variant="outlined"
                            size="small"
                            value={pendingFilters[col.key] || ""}
                            onChange={(e) => {
                              if (!isLocked) {
                                handlePendingFilterChange(col.key, e.target.value);
                              }
                            }}
                            InputProps={{
                              startAdornment: (
                                <Search
                                  size={16}
                                  style={{
                                    marginRight: "8px",
                                    color: colors.grey[800],
                                  }}
                                />
                              ),
                              readOnly: isLocked, // prevents typing
                            }}
                            disabled={isLocked} // visually disables input
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: isLocked
                                  ? theme.palette.mode === "dark"
                                    ? colors.grey[800]
                                    : colors.grey[200]
                                  : colors.surface,
                              },
                              "& .MuiInputLabel-root.Mui-disabled": {
                                color: colors.text.secondary,
                              },
                            }}
                          />
                        );
                      })}
                  </Box>
                </div>
                <div className="ems-flt-foot">
                  <button className="ems-btn ems-ghost" onClick={clearAllFilters}>
                    <X size={16} />
                    Clear All Filters
                  </button>
                  <div className="ems-flt-spacer" />
                  <button className="ems-btn ems-primary" onClick={applyFilters}>
                    <Filter size={16} />
                    Apply Filters
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {columnManagerOpen && (
        <Dialog
          open={columnManagerOpen}
          onClose={handleColumnDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              backgroundColor: colors.grey[300],
              color: colors.text.primary,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",

                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                component="span"
                sx={{ fontWeight: 500 }}
              >
                Manage Columns
              </Typography>
              <IconButton
                title="Close"
                onClick={handleColumnDialogClose}
                sx={{ color: colors.text.primary }}
              >
                <X size={20} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 3,
              pb: 0,
              backgroundColor: colors.surface,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allPendingColumnsVisible}
                  onChange={handleSelectAllPendingColumns}
                  sx={{ color: colors.success }}
                />
              }
              label={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, color: colors.text.primary }}
                >
                  Select All
                </Typography>
              }
              sx={{ mb: 2 }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 2,
                mb: 3,
              }}
            >
              {pendingColumns.map((col) => (
                <Box
                  key={col.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: colors.grey[50],
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={col.visible}
                        onChange={() => togglePendingColumnVisibility(col.key)}
                        disabled={col.mandatory}
                        sx={{ color: colors.success }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{ color: colors.text.primary }}
                      >
                        {formatLabel(col.label)}
                      </Typography>
                    }
                    sx={{ flex: 1, m: 0 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => togglePendingColumnPinned(col.key)}
                    title={
                      col.pinned === "left" ? "Unpin column" : "Pin column"
                    }
                    sx={{
                      color:
                        col.pinned === "left"
                          ? colors.primary
                          : colors.text.secondary,
                      "&:hover": {
                        backgroundColor: colors.grey[200],
                      },
                    }}
                  >
                    <PushPinIcon
                      fontSize="small"
                      sx={{
                        transform:
                          col.pinned === "left"
                            ? "rotate(-20deg)"
                            : "rotate(45deg)",
                        transition: "transform 0.2s ease-in-out",
                      }}
                    />
                  </IconButton>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "flex-start",
                flexWrap: "wrap",
                position: "sticky",
                background: "white",
                bottom: 0,
                p: 2,
              }}
            >
              <Button
                variant="contained"
                title="Apply changes"
                sx={{
                  backgroundColor: colors.primary,
                  color: "white",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.light
                        : theme.palette.primary.dark,
                  },
                }}
                startIcon={<Columns3 size={16} />}
                onClick={applyColumnChanges}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                title="Cancel changes"
                sx={{
                  color: colors.text.secondary,
                  borderColor: colors.text.secondary,
                  "&:hover": {
                    borderColor: colors.text.primary,
                    color: colors.text.primary,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                startIcon={<X size={16} />}
                onClick={cancelColumnChanges}
              >
                Cancel
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {isSmallScreen ? (
        <Box>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={24} sx={{ color: colors.primary }} />
              <Typography
                variant="body2"
                sx={{ mt: 1, ml: 2, color: colors.text.secondary }}
              >
                Loading data...
              </Typography>
            </Box>
          ) : paginatedData?.length === 0 ? (
            <Paper
              elevation={1}
              sx={{
                p: 4,
                textAlign: "center",
                backgroundColor: colors.surface,
              }}
            >
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                No records found.
              </Typography>
            </Paper>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns="1fr"
              gap={1.5}
              sx={{ p: 0.5, width: "100%", maxWidth: "100%" }}
            >
              {paginatedData?.map((row, index) => {
                if (leadsTable) {
                  const name = String(getNestedValue(row, "person_full_name") ?? "").trim() || "—";
                  const trainingProg = (() => { const v = String(getNestedValue(row, "training_program") ?? "").trim(); return v && v !== "-" ? v : ""; })();
                  const leadStage = (() => { const v = String(getNestedValue(row, "lead_stage_name") ?? "").trim(); return v && v !== "-" ? v : ""; })();
                  const leadSource = (() => { const v = String(getNestedValue(row, "__lead_source_display") ?? "").trim(); return v && v !== "—" && v !== "-" ? v : ""; })();
                  const leadDatetime = String(getNestedValue(row, "lead_datetime") ?? "").trim();
                  const feeRaw = getNestedValue(row, "training_program_fee");
                  const fee = feeRaw != null && feeRaw !== "" && !isNaN(Number(feeRaw)) ? Number(feeRaw) : null;
                  const ap = name.split(/\s+/).filter((p) => /^[a-zA-Z]/.test(p));
                  const initials = ap.length >= 2 ? (ap[0][0] + ap[1][0]).toUpperCase() : ap.length === 1 ? ap[0].slice(0, 2).toUpperCase() : "?";
                  const stageLower = leadStage.toLowerCase();
                  let stageBg = "#f3f4f6", stageTxt = "#374151", stageDot = "#9ca3af";
                  if (stageLower.includes("hot")) { stageBg = "#fce7f3"; stageTxt = "#be185d"; stageDot = "#ec4899"; }
                  else if (stageLower.includes("warm")) { stageBg = "#fff7ed"; stageTxt = "#c2410c"; stageDot = "#f97316"; }
                  else if (stageLower.includes("cold")) { stageBg = "#dbeafe"; stageTxt = "#1d4ed8"; stageDot = "#3b82f6"; }
                  else if (stageLower.includes("qualified")) { stageBg = "#dcfce7"; stageTxt = "#15803d"; stageDot = "#22c55e"; }
                  else if (stageLower.includes("demo")) { stageBg = "#ede9fe"; stageTxt = "#6d28d9"; stageDot = "#8b5cf6"; }
                  else if (stageLower.includes("contact")) { stageBg = "#cffafe"; stageTxt = "#0e7490"; stageDot = "#06b6d4"; }
                  else if (stageLower.includes("follow")) { stageBg = "#fef3c7"; stageTxt = "#b45309"; stageDot = "#f59e0b"; }
                  const timeAgo = (() => {
                    if (!leadDatetime || leadDatetime === "-") return "";
                    const m = leadDatetime.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
                    if (!m) return "";
                    let h = parseInt(m[4]); const mn = parseInt(m[5]); const ampm = m[6].toUpperCase();
                    if (ampm === "PM" && h !== 12) h += 12;
                    if (ampm === "AM" && h === 12) h = 0;
                    const d = new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]), h, mn);
                    if (isNaN(d.getTime())) return "";
                    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
                    if (diff < 60) return `${diff}s ago`;
                    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
                    return `${Math.floor(diff / 604800)}w ago`;
                  })();
                  const srcLower = leadSource.toLowerCase();
                  const srcIconStyle = { width: 13, height: 13, flexShrink: 0 };
                  let srcIcon;
                  if (srcLower.includes("instagram")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/></svg>;
                  else if (srcLower.includes("facebook")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>;
                  else if (srcLower.includes("linkedin")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>;
                  else if (srcLower.includes("whatsapp")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>;
                  else if (srcLower.includes("email") || srcLower.includes("mail")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/></svg>;
                  else if (srcLower.includes("phone") || srcLower.includes("call")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.9 9.9a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.83 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 5.61 5.61l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.99 16.92z"/></svg>;
                  else if (srcLower.includes("referral") || srcLower.includes("refer")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
                  else srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>;
                  return (
                    <Box key={index} onClick={() => onclickRow && onclickRow(row)} sx={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", p: "16px", display: "flex", flexDirection: "column", gap: 1.25, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow .18s", cursor: onclickRow ? "pointer" : "default", "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.10)" } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Box sx={{ width: 42, height: 42, borderRadius: "50%", background: "#4C5565", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700, flexShrink: 0, letterSpacing: "0.03em", userSelect: "none" }}>{initials}</Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ fontWeight: 700, fontSize: "0.925rem", color: "#111827", lineHeight: 1.25, wordBreak: "break-word" }}>{name}</Box>
                          {trainingProg && <Box sx={{ fontSize: "0.775rem", color: "#6b7280", mt: 0.2, wordBreak: "break-word" }}>{trainingProg}</Box>}
                        </Box>
                      </Box>
                      {(leadStage || leadSource) && (
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                          {leadStage && <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.35, borderRadius: "999px", background: stageBg, fontSize: "0.75rem", fontWeight: 600, color: stageTxt }}><Box sx={{ width: 6, height: 6, borderRadius: "50%", background: stageDot, flexShrink: 0 }} />{leadStage}</Box>}
                          {leadSource && <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.35, borderRadius: "999px", background: "#f3f4f6", fontSize: "0.75rem", fontWeight: 500, color: "#374151" }}>{srcIcon}{leadSource}</Box>}
                        </Box>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 0.75, borderTop: "1px solid #f3f4f6" }}>
                        <Box sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}>{fee != null ? `₹${fee.toLocaleString("en-IN")}` : ""}</Box>
                        {timeAgo && <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.75rem", color: "#9ca3af" }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>{timeAgo}</Box>}
                      </Box>
                      {hasActionColumn && <Box sx={{ display: "flex", gap: 0.5 }}>{renderRowActions(row, { stopPropagation: true })}</Box>}
                    </Box>
                  );
                }
                if (isStudentsTable) {
                  const name = String(getNestedValue(row, "student_name") ?? "").trim() || "—";
                  const studentId = String(getNestedValue(row, "student_id") ?? "").trim();
                  const status = String(getNestedValue(row, "student_status") ?? "").trim();
                  const program = String(getNestedValue(row, "student_training_program") ?? "").trim();
                  const programCount = Number(getNestedValue(row, "student_program_count") ?? 0);
                  const city = String(getNestedValue(row, "city") ?? "").trim();
                  const country = String(getNestedValue(row, "country") ?? "").trim();
                  const location = [city, country].filter((p) => p && p !== "-").join(", ") || "—";
                  const phone = String(getNestedValue(row, "phone") ?? "").trim();
                  const pendingRaw = getNestedValue(row, "pending_fee");
                  const pendingIsCleared = pendingRaw === "Cleared";
                  const pendingAmt = !pendingIsCleared && pendingRaw != null && !isNaN(Number(pendingRaw)) ? Number(pendingRaw) : null;
                  const ap = name.split(/\s+/).filter((p) => /^[a-zA-Z]/.test(p));
                  const initials = ap.length >= 2 ? (ap[0][0] + ap[1][0]).toUpperCase() : ap.length === 1 ? ap[0].slice(0, 2).toUpperCase() : "?";
                  const statusLower = status.toLowerCase();
                  let statusBg = "#f3f4f6", statusTxt = "#374151", statusDot = "#9ca3af";
                  if (statusLower.includes("active")) { statusBg = "#cffafe"; statusTxt = "#0e7490"; statusDot = "#06b6d4"; }
                  else if (statusLower.includes("complet")) { statusBg = "#dcfce7"; statusTxt = "#15803d"; statusDot = "#22c55e"; }
                  else if (statusLower.includes("drop") || statusLower.includes("inactive")) { statusBg = "#fee2e2"; statusTxt = "#b91c1c"; statusDot = "#ef4444"; }
                  else if (statusLower.includes("hold") || statusLower.includes("pause")) { statusBg = "#fef3c7"; statusTxt = "#b45309"; statusDot = "#f59e0b"; }
                  const fields = [
                    { label: "Program", value: program && program !== "-" ? program : null, extra: programCount > 1 ? `${programCount} programs` : null },
                    { label: "Location", value: location !== "—" ? location : null },
                    { label: "Phone", value: phone && phone !== "-" ? phone : null },
                    { label: "Pending", value: pendingIsCleared ? "Cleared" : pendingAmt != null ? `₹${pendingAmt.toLocaleString("en-IN")}` : null, isCleared: pendingIsCleared, isPending: pendingAmt != null && !pendingIsCleared },
                  ].filter((f) => f.value != null);
                  return (
                    <Box key={index} onClick={() => onclickRow && onclickRow(row)} sx={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", p: "16px", display: "flex", flexDirection: "column", gap: 1.25, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow .18s", cursor: onclickRow ? "pointer" : "default", "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.10)" } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Box sx={{ width: 42, height: 42, borderRadius: "50%", background: "#0d9488", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700, flexShrink: 0, letterSpacing: "0.03em", userSelect: "none" }}>{initials}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ fontWeight: 700, fontSize: "0.925rem", color: "#111827", lineHeight: 1.25, wordBreak: "break-word" }}>{name}</Box>
                          {studentId && studentId !== "-" && <Box sx={{ fontSize: "0.775rem", color: "#6b7280", mt: 0.2 }}>Adm #{studentId}</Box>}
                        </Box>
                        {status && <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.35, borderRadius: "999px", background: statusBg, fontSize: "0.75rem", fontWeight: 600, color: statusTxt, flexShrink: 0 }}><Box sx={{ width: 6, height: 6, borderRadius: "50%", background: statusDot, flexShrink: 0 }} />{status}</Box>}
                      </Box>
                      {fields.length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, pt: 1, borderTop: "1px solid #f3f4f6" }}>
                          {fields.map((f) => (
                            <Box key={f.label} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.85rem", minWidth: 0 }}>
                              <Box sx={{ color: "#9ca3af", minWidth: 70, maxWidth: 70, flexShrink: 0, fontSize: "0.78rem" }}>{f.label}</Box>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box component="span" sx={{ fontWeight: 600, color: f.isCleared ? "#15803d" : f.isPending ? "#e11d48" : "#111827", fontSize: "0.875rem" }}>{f.value}</Box>
                                {f.extra && <Box component="span" sx={{ fontSize: "0.75rem", color: "#6b7280", ml: 0.75 }}>{f.extra}</Box>}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {hasActionColumn && <Box sx={{ display: "flex", gap: 0.5, pt: 0.25 }}>{renderRowActions(row, { stopPropagation: true })}</Box>}
                    </Box>
                  );
                }
                const CARD_NAME_KEYS = ["lead_person_name", "activity_by", "student_name", "student", "batch_student", "trainer", "lead", "employee_name", "application_user_full_name"];
                const isEmpNameCol = (col) => col.key === "name" && String(tableName || "").toLowerCase().includes("employee");
                const nameCols = visibleColumns.filter((col) => CARD_NAME_KEYS.includes(col.key) || isEmpNameCol(col));
                const primaryNameCol = nameCols[0];
                const secondaryNameCol = nameCols[1];
                const STATUS_KEYS = ["status", "call_status", "employment_status_name", "student_status", "class_status", "admission_status", "payment_status", "batch_status", "result_status"];
                const statusCol = visibleColumns.find((col) => STATUS_KEYS.includes(col.key));
                const otherCols = visibleColumns
                  .filter((col) => col !== primaryNameCol && col !== secondaryNameCol && col !== statusCol)
                  .slice(0, 6);
                const primaryName = primaryNameCol ? String(getNestedValue(row, primaryNameCol.key) ?? "").trim() || "—" : null;
                const secondaryName = secondaryNameCol ? String(getNestedValue(row, secondaryNameCol.key) ?? "").trim() || "" : "";
                let initials = "?";
                if (primaryName && primaryName !== "—") {
                  const ap = primaryName.split(/\s+/).filter((p) => /^[a-zA-Z]/.test(p));
                  initials = ap.length >= 2 ? (ap[0][0] + ap[1][0]).toUpperCase() : ap.length === 1 ? ap[0].slice(0, 2).toUpperCase() : primaryName.slice(0, 2).toUpperCase();
                }
                return (
                  <Box key={index} onClick={() => onclickRow && onclickRow(row)} sx={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", p: 2, display: "flex", flexDirection: "column", gap: 1.5, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow .18s", cursor: onclickRow ? "pointer" : "default", "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.10)" }, width: "100%", minWidth: 0, boxSizing: "border-box" }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
                      {primaryName !== null && <Box sx={{ width: 40, height: 40, borderRadius: "50%", background: "#4C5565", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700, flexShrink: 0, letterSpacing: "0.03em", userSelect: "none" }}>{initials}</Box>}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontWeight: 700, fontSize: "0.925rem", color: "#111827", lineHeight: 1.3, wordBreak: "break-word" }}>{primaryName ?? (visibleColumns[0] ? String(getNestedValue(row, visibleColumns[0].key) ?? "") : "—")}</Box>
                        {secondaryName && <Box sx={{ fontSize: "0.8rem", color: "#6b7280", mt: 0.3, wordBreak: "break-word" }}>{secondaryName}</Box>}
                      </Box>
                      {statusCol && <Box sx={{ flexShrink: 0, maxWidth: "42%" }}>{renderDataCellContent(row, statusCol)}</Box>}
                    </Box>
                    {otherCols.length > 0 && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.65, pt: 1, borderTop: "1px solid #f3f4f6" }}>
                        {otherCols.map((col) => {
                          const content = renderDataCellContent(row, col);
                          const raw = getNestedValue(row, col.key);
                          const fallback = raw === null || raw === undefined || String(raw).trim() === "" ? "—" : String(raw);
                          return (
                            <Box key={col.key} sx={{ display: "flex", alignItems: "flex-start", gap: 1, fontSize: "0.85rem", minWidth: 0 }}>
                              <Box sx={{ color: "#9ca3af", minWidth: 72, maxWidth: 88, flexShrink: 0, fontSize: "0.78rem", textTransform: "capitalize" }}>{formatLabel(col.label || col.key)}</Box>
                              <Box sx={{ color: "#374151", fontWeight: 500, minWidth: 0, flex: 1, wordBreak: "break-word" }}>{content || fallback}</Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    {hasActionColumn && <Box sx={{ display: "flex", gap: 0.5, pt: 0.25, flexWrap: "wrap" }}>{renderRowActions(row, { stopPropagation: true })}</Box>}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      ) : (
        <div className={view === "table" ? "ems-card ems-table-card" : "ems-table-card ems-grid-bg"}>
          {view === "table" ? (
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="dynamic data grid">
                {/* ---------- TABLE HEADER ---------- */}
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.grey[100] }}>
                    {isStudentsTable && <StudentExpandHeaderCell colors={colors} />}

                    {/* ---------- HEADER COLUMNS ---------- */}
                    {visibleColumns.map((col, index) => (
                      <TableCell
                        key={col.key}
                        sx={{
                          p: 2,
                          textAlign: "left",
                          fontWeight: 700,
                          fontSize: "14px",
                          color: colors.text.primary,
                          borderRight: `1px solid ${colors.grey[300]}`,
                          minWidth: col.width,
                          maxWidth: col.width,
                          position:
                            col.pinned === "left" ? "sticky" : "relative",
                          left: col.pinned === "left" ? 0 : "auto",
                          backgroundColor: colors.grey[100],
                          zIndex: col.pinned === "left" ? 2 : undefined,
                          userSelect: "none",
                          textTransform: "capitalize",
                          letterSpacing: "0.08333em",
                          cursor:
                            resizingColumnKey === col.key
                              ? "col-resize"
                              : "default",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const dropIndex = index;
                          const dragIndex = Number.parseInt(
                            e.dataTransfer.getData("text/plain"),
                            10
                          );
                          if (dragIndex !== dropIndex) {
                            handleColumnReorder(dragIndex, dropIndex);
                          }
                          setDraggedColumnIndex(null);
                        }}
                      >
                        <Box
                          component="span"
                          title={`Click to sort by ${formatLabel(col.label)} or drag to reorder`}
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            "&:hover": {
                              cursor:
                                col.pinned === "left" ? "default" : "grab",
                            },
                            "&:active": {
                              cursor:
                                col.pinned === "left" ? "default" : "grabbing",
                            },
                          }}
                          draggable={col.pinned !== "left"}
                          onDragStart={(e) => {
                            if (col.pinned === "left") {
                              e.preventDefault();
                              return;
                            }
                            e.dataTransfer.setData(
                              "text/plain",
                              index.toString()
                            );
                            setDraggedColumnIndex(index);
                          }}
                          onDragEnd={() => setDraggedColumnIndex(null)}
                          onClick={() => handleSort(col.key)}
                        >
                          {formatLabel(col.label)}
                          {col.pinned === "left" && (
                            <PushPinIcon
                              fontSize="small"
                              sx={{
                                ml: 0.5,
                                color: "black",
                                opacity: 0.8,
                                transform: "rotate(-20deg)",
                              }}
                              titleAccess="Pinned column"
                            />
                          )}
                          {renderColumnSortIcon(col)}
                        </Box>
                        <Box
                          sx={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: "16px",
                            cursor: "col-resize",
                            backgroundColor:
                              resizingColumnKey === col.key
                                ? colors.primary
                                : "transparent",
                            opacity: resizingColumnKey === col.key ? 0.5 : 0.2,
                            "&:hover": {
                              opacity: 1,
                              backgroundColor: colors.primary,
                            },
                            transition:
                              "opacity 0.2s ease-in-out, background-color 0.2s ease-in-out",
                            zIndex: 2,
                          }}
                          onMouseDown={(e) => handleMouseDown(e, col.key)}
                        />
                      </TableCell>
                    ))}

                    {hasActionColumn && (
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: colors.grey[100],
                          position: "sticky",
                          right: 0,
                          zIndex: 2,
                          borderBottom: `1px solid ${colors.grey[300]}`,
                        }}
                      />
                    )}
                  </TableRow>
                </TableHead>

                {/* ---------- TABLE BODY ---------- */}
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={
                          visibleColumns.length +
                          (hasActionColumn ? 1 : 0) +
                          studentTableExtraCols
                        }
                        sx={{ textAlign: "center", py: 3 }}
                      >
                        <CircularProgress
                          size={24}
                          sx={{ color: colors.primary }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, ml: 2, color: colors.text.secondary }}
                        >
                          Loading data...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : paginatedData?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={
                          visibleColumns.length +
                          (hasActionColumn ? 1 : 0) +
                          studentTableExtraCols
                        }
                        sx={{ textAlign: "center", py: 3 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: colors.text.secondary }}
                        >
                          No records found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData?.map((row, rowIndex) => {
                      const rowPrimaryId = getStudentRowId(row, mainKey, rowIndex);
                      const isStudentExpanded = !!expandedStudentRows[rowPrimaryId];
                      const studentAccordionRows = isStudentsTable
                        ? getStudentAccordionRows(row)
                        : [];
                      const studentRowColSpan =
                        visibleColumns.length +
                        (hasActionColumn ? 1 : 0) +
                        studentTableExtraCols;
                      return (
                        <Fragment key={rowPrimaryId}>
                        <TableRow
                          hover
                          sx={{
                            "& .pinned-column": {
                              backgroundColor: `${colors.background} !important`,
                            },
                            "&:hover .pinned-column": {
                              backgroundColor: `${theme.palette.action.hover} !important`,
                            },
                          }}
                        >
                          {isStudentsTable && (
                            <StudentExpandToggleCell
                              expanded={isStudentExpanded}
                              onToggle={() => toggleStudentRow(row, rowIndex)}
                              colors={colors}
                            />
                          )}

                          {/* ✅ Data cells */}
                          {visibleColumns.map((col) => (
                            <TableCell
                              key={col.key}
                              className={
                                col.pinned === "left" ? "pinned-column" : ""
                              }
                              sx={{
                                p: 2,
                                fontSize: "14px",
                                color: colors.text.primary,
                                position:
                                  col.pinned === "left" ? "sticky" : "relative",
                                left: col.pinned === "left" ? 0 : "auto",
                                backgroundColor:
                                  col.pinned === "left"
                                    ? colors.background
                                    : "inherit",
                                transition: "background-color 0.2s ease",
                                zIndex: col.pinned === "left" ? 1 : undefined,
                                borderBottom: `1px solid ${colors.grey[200]}`,
                                whiteSpace: leadsTable ? "normal" : "nowrap",
                                overflow: leadsTable ? "visible" : "hidden",
                                textOverflow: leadsTable ? "clip" : "ellipsis",
                                maxWidth: col.width || 150,
                              }}
                            >
                              {renderDataCellContent(row, col)}
                            </TableCell>
                          ))}

                          {/* ✅ Actions */}
                          {hasActionColumn && (
                            <TableCell
                              sx={{
                                p: 2,
                                textAlign: "center",
                                borderBottom: `1px solid ${colors.grey[200]}`,
                                minWidth: actionColumnWidth,
                                maxWidth: actionColumnWidth,
                                position: "sticky",
                                right: 0,
                                backgroundColor: colors.background,
                                zIndex: 1,
                                verticalAlign: leadsTable ? "top" : undefined,
                              }}
                            >
                              {wrapActionCellWithActiveToggle(
                                row,
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 0.5,
                                    justifyContent: "flex-end",
                                    alignItems: leadsTable ? "flex-start" : undefined,
                                    flexWrap: "nowrap",
                                    overflow: "hidden",
                                  }}
                                >
                                  {renderRowActions(row, { stopPropagation: true })}
                                </Box>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                        {isStudentsTable && isStudentExpanded && (
                          <StudentAccordionCollapseRow
                            expanded={isStudentExpanded}
                            colSpan={studentRowColSpan}
                            accordionRows={studentAccordionRows}
                            colors={colors}
                          />
                        )}
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
              gap={2}
              sx={{ p: 0.5 }}
            >
              {paginatedData?.map((row, index) => {
                /* ---- LEADS-SPECIFIC CARD ---- */
                if (leadsTable) {
                  const name = String(getNestedValue(row, "person_full_name") ?? "").trim() || "—";
                  const trainingProg = (() => { const v = String(getNestedValue(row, "training_program") ?? "").trim(); return v && v !== "-" ? v : ""; })();
                  const leadStage = (() => { const v = String(getNestedValue(row, "lead_stage_name") ?? "").trim(); return v && v !== "-" ? v : ""; })();
                  const leadSource = (() => { const v = String(getNestedValue(row, "__lead_source_display") ?? "").trim(); return v && v !== "—" && v !== "-" ? v : ""; })();
                  const leadDatetime = String(getNestedValue(row, "lead_datetime") ?? "").trim();
                  const feeRaw = getNestedValue(row, "training_program_fee");
                  const fee = feeRaw != null && feeRaw !== "" && !isNaN(Number(feeRaw)) ? Number(feeRaw) : null;

                  const ap = name.split(/\s+/).filter((p) => /^[a-zA-Z]/.test(p));
                  const initials = ap.length >= 2 ? (ap[0][0] + ap[1][0]).toUpperCase() : ap.length === 1 ? ap[0].slice(0, 2).toUpperCase() : "?";

                  const stageLower = leadStage.toLowerCase();
                  let stageBg = "#f3f4f6", stageTxt = "#374151", stageDot = "#9ca3af";
                  if (stageLower.includes("hot")) { stageBg = "#fce7f3"; stageTxt = "#be185d"; stageDot = "#ec4899"; }
                  else if (stageLower.includes("warm")) { stageBg = "#fff7ed"; stageTxt = "#c2410c"; stageDot = "#f97316"; }
                  else if (stageLower.includes("cold")) { stageBg = "#dbeafe"; stageTxt = "#1d4ed8"; stageDot = "#3b82f6"; }
                  else if (stageLower.includes("qualified")) { stageBg = "#dcfce7"; stageTxt = "#15803d"; stageDot = "#22c55e"; }
                  else if (stageLower.includes("demo")) { stageBg = "#ede9fe"; stageTxt = "#6d28d9"; stageDot = "#8b5cf6"; }
                  else if (stageLower.includes("contact")) { stageBg = "#cffafe"; stageTxt = "#0e7490"; stageDot = "#06b6d4"; }
                  else if (stageLower.includes("follow")) { stageBg = "#fef3c7"; stageTxt = "#b45309"; stageDot = "#f59e0b"; }

                  const timeAgo = (() => {
                    if (!leadDatetime || leadDatetime === "-") return "";
                    const m = leadDatetime.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
                    if (!m) return "";
                    let h = parseInt(m[4]); const mn = parseInt(m[5]); const ampm = m[6].toUpperCase();
                    if (ampm === "PM" && h !== 12) h += 12;
                    if (ampm === "AM" && h === 12) h = 0;
                    const d = new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]), h, mn);
                    if (isNaN(d.getTime())) return "";
                    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
                    if (diff < 60) return `${diff}s ago`;
                    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
                    return `${Math.floor(diff / 604800)}w ago`;
                  })();

                  const srcLower = leadSource.toLowerCase();
                  const srcIconStyle = { width: 13, height: 13, flexShrink: 0 };
                  let srcIcon;
                  if (srcLower.includes("instagram")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/></svg>;
                  else if (srcLower.includes("facebook")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>;
                  else if (srcLower.includes("linkedin")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>;
                  else if (srcLower.includes("whatsapp")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>;
                  else if (srcLower.includes("email") || srcLower.includes("mail")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/></svg>;
                  else if (srcLower.includes("phone") || srcLower.includes("call")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.9 9.9a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.83 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 5.61 5.61l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.99 16.92z"/></svg>;
                  else if (srcLower.includes("referral") || srcLower.includes("refer")) srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
                  else srcIcon = <svg style={srcIconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>;

                  return (
                    <Box
                      key={index}
                      onClick={() => onclickRow && onclickRow(row)}
                      sx={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", p: "16px", display: "flex", flexDirection: "column", gap: 1.25, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow .18s", cursor: onclickRow ? "pointer" : "default", "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.10)" } }}
                    >
                      {/* Header: avatar + name + training program */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Box sx={{ width: 42, height: 42, borderRadius: "50%", background: "#4C5565", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700, flexShrink: 0, letterSpacing: "0.03em", userSelect: "none" }}>
                          {initials}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ fontWeight: 700, fontSize: "0.925rem", color: "#111827", lineHeight: 1.25, wordBreak: "break-word" }}>{name}</Box>
                          {trainingProg && <Box sx={{ fontSize: "0.775rem", color: "#6b7280", mt: 0.2, wordBreak: "break-word" }}>{trainingProg}</Box>}
                        </Box>
                      </Box>

                      {/* Stage + Source chips */}
                      {(leadStage || leadSource) && (
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                          {leadStage && (
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.35, borderRadius: "999px", background: stageBg, fontSize: "0.75rem", fontWeight: 600, color: stageTxt }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: stageDot, flexShrink: 0 }} />
                              {leadStage}
                            </Box>
                          )}
                          {leadSource && (
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.35, borderRadius: "999px", background: "#f3f4f6", fontSize: "0.75rem", fontWeight: 500, color: "#374151" }}>
                              {srcIcon}
                              {leadSource}
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Footer: fee + time */}
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 0.75, borderTop: "1px solid #f3f4f6" }}>
                        <Box sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}>
                          {fee != null ? `₹${fee.toLocaleString("en-IN")}` : ""}
                        </Box>
                        {timeAgo && (
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.75rem", color: "#9ca3af" }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            {timeAgo}
                          </Box>
                        )}
                      </Box>

                      {hasActionColumn && (
                        <Box sx={{ display: "flex", gap: 0.5 }}>{renderRowActions(row, { stopPropagation: true })}</Box>
                      )}
                    </Box>
                  );
                }

                /* ---- STUDENTS-SPECIFIC CARD ---- */
                if (isStudentsTable) {
                  const name = String(getNestedValue(row, "student_name") ?? "").trim() || "—";
                  const studentId = String(getNestedValue(row, "student_id") ?? "").trim();
                  const status = String(getNestedValue(row, "student_status") ?? "").trim();
                  const program = String(getNestedValue(row, "student_training_program") ?? "").trim();
                  const programCount = Number(getNestedValue(row, "student_program_count") ?? 0);
                  const city = String(getNestedValue(row, "city") ?? "").trim();
                  const country = String(getNestedValue(row, "country") ?? "").trim();
                  const location = [city, country].filter((p) => p && p !== "-").join(", ") || "—";
                  const phone = String(getNestedValue(row, "phone") ?? "").trim();
                  const pendingRaw = getNestedValue(row, "pending_fee");
                  const pendingIsCleared = pendingRaw === "Cleared";
                  const pendingAmt = !pendingIsCleared && pendingRaw != null && !isNaN(Number(pendingRaw)) ? Number(pendingRaw) : null;

                  const ap = name.split(/\s+/).filter((p) => /^[a-zA-Z]/.test(p));
                  const initials = ap.length >= 2 ? (ap[0][0] + ap[1][0]).toUpperCase() : ap.length === 1 ? ap[0].slice(0, 2).toUpperCase() : "?";

                  const statusLower = status.toLowerCase();
                  let statusBg = "#f3f4f6", statusTxt = "#374151", statusDot = "#9ca3af";
                  if (statusLower.includes("active")) { statusBg = "#cffafe"; statusTxt = "#0e7490"; statusDot = "#06b6d4"; }
                  else if (statusLower.includes("complet")) { statusBg = "#dcfce7"; statusTxt = "#15803d"; statusDot = "#22c55e"; }
                  else if (statusLower.includes("drop") || statusLower.includes("inactive")) { statusBg = "#fee2e2"; statusTxt = "#b91c1c"; statusDot = "#ef4444"; }
                  else if (statusLower.includes("hold") || statusLower.includes("pause")) { statusBg = "#fef3c7"; statusTxt = "#b45309"; statusDot = "#f59e0b"; }

                  const fields = [
                    { label: "Program", value: program && program !== "-" ? program : null, extra: programCount > 1 ? `${programCount} programs` : null },
                    { label: "Location", value: location !== "—" ? location : null },
                    { label: "Phone", value: phone && phone !== "-" ? phone : null },
                    { label: "Pending", value: pendingIsCleared ? "Cleared" : pendingAmt != null ? `₹${pendingAmt.toLocaleString("en-IN")}` : null, isCleared: pendingIsCleared, isPending: pendingAmt != null && !pendingIsCleared },
                  ].filter((f) => f.value != null);

                  return (
                    <Box key={index} onClick={() => onclickRow && onclickRow(row)} sx={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", p: "16px", display: "flex", flexDirection: "column", gap: 1.25, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow .18s", cursor: onclickRow ? "pointer" : "default", "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.10)" } }}>
                      {/* Header */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Box sx={{ width: 42, height: 42, borderRadius: "50%", background: "#0d9488", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700, flexShrink: 0, letterSpacing: "0.03em", userSelect: "none" }}>
                          {initials}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ fontWeight: 700, fontSize: "0.925rem", color: "#111827", lineHeight: 1.25, wordBreak: "break-word" }}>{name}</Box>
                          {studentId && studentId !== "-" && (
                            <Box sx={{ fontSize: "0.775rem", color: "#6b7280", mt: 0.2 }}>Adm #{studentId}</Box>
                          )}
                        </Box>
                        {status && (
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.35, borderRadius: "999px", background: statusBg, fontSize: "0.75rem", fontWeight: 600, color: statusTxt, flexShrink: 0 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: statusDot, flexShrink: 0 }} />
                            {status}
                          </Box>
                        )}
                      </Box>

                      {/* Field rows */}
                      {fields.length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, pt: 1, borderTop: "1px solid #f3f4f6" }}>
                          {fields.map((f) => (
                            <Box key={f.label} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.85rem", minWidth: 0 }}>
                              <Box sx={{ color: "#9ca3af", minWidth: 70, maxWidth: 70, flexShrink: 0, fontSize: "0.78rem" }}>{f.label}</Box>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box component="span" sx={{ fontWeight: 600, color: f.isCleared ? "#15803d" : f.isPending ? "#e11d48" : "#111827", fontSize: "0.875rem" }}>{f.value}</Box>
                                {f.extra && <Box component="span" sx={{ fontSize: "0.75rem", color: "#6b7280", ml: 0.75 }}>{f.extra}</Box>}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {hasActionColumn && <Box sx={{ display: "flex", gap: 0.5, pt: 0.25 }}>{renderRowActions(row, { stopPropagation: true })}</Box>}
                    </Box>
                  );
                }

                /* ---- GENERIC CARD ---- */
                const CARD_NAME_KEYS = ["lead_person_name", "activity_by", "student_name", "student", "batch_student", "trainer", "lead", "employee_name", "application_user_full_name"];
                const isEmpNameCol = (col) => col.key === "name" && String(tableName || "").toLowerCase().includes("employee");
                const nameCols = visibleColumns.filter((col) => CARD_NAME_KEYS.includes(col.key) || isEmpNameCol(col));
                const primaryNameCol = nameCols[0];
                const secondaryNameCol = nameCols[1];
                const STATUS_KEYS = ["status", "call_status", "employment_status_name", "student_status", "class_status", "admission_status", "payment_status", "batch_status", "result_status"];
                const statusCol = visibleColumns.find((col) => STATUS_KEYS.includes(col.key));
                const otherCols = visibleColumns.filter((col) => col !== primaryNameCol && col !== secondaryNameCol && col !== statusCol);
                const primaryName = primaryNameCol ? String(getNestedValue(row, primaryNameCol.key) ?? "").trim() || "—" : null;
                const secondaryName = secondaryNameCol ? String(getNestedValue(row, secondaryNameCol.key) ?? "").trim() || "" : "";
                let initials = "?";
                if (primaryName && primaryName !== "—") {
                  const ap = primaryName.split(/\s+/).filter((p) => /^[a-zA-Z]/.test(p));
                  initials = ap.length >= 2 ? (ap[0][0] + ap[1][0]).toUpperCase() : ap.length === 1 ? ap[0].slice(0, 2).toUpperCase() : primaryName.slice(0, 2).toUpperCase();
                }
                return (
                  <Box key={index} onClick={() => onclickRow && onclickRow(row)} sx={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", p: 2, display: "flex", flexDirection: "column", gap: 1.5, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow .18s", cursor: onclickRow ? "pointer" : "default", "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.10)" } }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
                      {primaryName !== null && (
                        <Box sx={{ width: 40, height: 40, borderRadius: "50%", background: "#4C5565", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700, flexShrink: 0, letterSpacing: "0.03em", userSelect: "none" }}>{initials}</Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontWeight: 700, fontSize: "0.925rem", color: "#111827", lineHeight: 1.3, wordBreak: "break-word" }}>{primaryName ?? (visibleColumns[0] ? String(getNestedValue(row, visibleColumns[0].key) ?? "") : "—")}</Box>
                        {secondaryName && <Box sx={{ fontSize: "0.8rem", color: "#6b7280", mt: 0.3, wordBreak: "break-word" }}>{secondaryName}</Box>}
                      </Box>
                      {statusCol && <Box sx={{ flexShrink: 0 }}>{renderDataCellContent(row, statusCol)}</Box>}
                    </Box>
                    {otherCols.length > 0 && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.65, pt: 1, borderTop: "1px solid #f3f4f6" }}>
                        {otherCols.map((col) => {
                          const content = renderDataCellContent(row, col);
                          const raw = getNestedValue(row, col.key);
                          const fallback = raw === null || raw === undefined || String(raw).trim() === "" ? "—" : String(raw);
                          return (
                            <Box key={col.key} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.85rem", minWidth: 0 }}>
                              <Box sx={{ color: "#9ca3af", minWidth: 88, maxWidth: 88, flexShrink: 0, fontSize: "0.78rem", textTransform: "capitalize" }}>{formatLabel(col.label || col.key)}</Box>
                              <Box sx={{ color: "#374151", fontWeight: 500, minWidth: 0, flex: 1 }}>{content || fallback}</Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    {hasActionColumn && <Box sx={{ display: "flex", gap: 0.5, pt: 0.25 }}>{renderRowActions(row, { stopPropagation: true })}</Box>}
                  </Box>
                );
              })}
            </Box>
          )}
        </div>
      )}

      <TablePagination
        paginationData={paginationData}
        theme={theme}
        isSmallScreen={isSmallScreen}
        colors={colors}
        paginatedDataLength={paginatedData.length}
        totalRecords={paginationData?.total ?? data.length}
        onPageChange={onPageChange}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(47, 54, 69, 0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 2.5,
            py: 2,
            backgroundColor: "#fff",
            color: "#0891B2",
            borderBottom: "1px solid #e8eaef",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              component="span"
              sx={{ fontWeight: 600, fontSize: "1.05rem" }}
            >
              Confirm Delete
            </Typography>
            <IconButton
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ color: "#6b7280", p: 0.5 }}
            >
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            backgroundColor: "#fff",
          }}
        >
          <Typography
            variant="body1"
            sx={{ color: "#4b5563", lineHeight: 1.6 }}
          >
            Are you sure you want to permanently delete this item? This action
            cannot be undone. This will also delete all the related data.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            pt: 0,
            backgroundColor: "#fff",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              color: "#6b7280",
              borderColor: "#d1d5db",
              minWidth: 100,
              fontWeight: 600,
              fontSize: "0.8125rem",
              borderRadius: "9px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteConfirm}
            sx={{
              backgroundColor: "#0891B2",
              color: "#fff",
              minWidth: 100,
              fontWeight: 600,
              fontSize: "0.8125rem",
              borderRadius: "9px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#077d9c",
                boxShadow: "none",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {snackbar.open && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 9999,
            maxWidth: 400,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 2,
              backgroundColor:
                snackbar.severity === "error"
                  ? colors.error
                  : snackbar.severity === "warning"
                    ? colors.warning
                    : snackbar.severity === "info"
                      ? colors.info
                      : colors.success,
              color: "white",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: "white" }}>
              {snackbar.message}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setSnackbar({ ...snackbar, open: false })}
              sx={{ color: "white", p: 0.5 }}
            >
              <X size={16} />
            </IconButton>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Customisetable;
